import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Edit, Delete, Add, Visibility, VisibilityOff, Search, RestaurantMenu, Category, CheckCircle, Cancel, Image as ImageIcon, BrokenImage, ArrowUpward, ArrowDownward, DragIndicator, FileDownload, FileUpload } from '@mui/icons-material';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import firebaseAdminService, { AdminMenuItem, AdminCategory, AdminAddOn, AdminModifierGroup } from '../../services/firebaseAdminService';
import ImportMenuDialog from './ImportMenuDialog';
import { auth } from '../../services/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';

// Small inline component for the brand switcher icons. Picks a light-on-dark
// or dark-on-mint asset based on the active state so the mark stays readable
// whether the pill is selected (bright mint bg) or idle (dark bg).
const BrandMark: React.FC<{ brand: 'all' | 'saheeh' | 'pizza_brand' | 'auto' | 'both'; active?: boolean; size?: number }> = ({ brand, active, size = 18 }) => {
  if (brand === 'auto') return <Box sx={{ fontSize: size * 0.72, lineHeight: 1 }}>{'✨'}</Box>;
  if (brand === 'both') return <Box sx={{ fontSize: size * 0.72, lineHeight: 1 }}>{'\u{1F310}'}</Box>;
  const src =
    brand === 'all' ? '/brand_icons/pos.png' :
    brand === 'saheeh' ? (active ? '/saheeh_logo_w.svg' : '/saheeh_logo_w.svg') :
    active ? '/brand_icons/pizza_brand_dark.png' : '/brand_icons/pizza_brand_light.png';
  return <Box component="img" src={src} alt="" sx={{ width: size, height: size, objectFit: 'contain', display: 'block' }} />;
};

interface MenuItem {
  id: string | number;
  name: string;
  name_de?: string;
  category?: string;
  price: number;
  available: boolean;
  popular?: boolean;
  sold_out?: boolean;
  display_order?: number;
  // Per-brand sort override. When set, the matching brand's storefront
  // uses this value instead of `display_order`.
  display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null;
  brand?: 'saheeh' | 'pizza_brand' | 'both' | null;
  description?: string;
  // German description — MUST be preserved alongside the English one so
  // the CSV export, storefront UI, and POS print all have localized
  // copy. Was accidentally dropped by the loader mappings until
  // 2026-04-24 when Owner noticed it missing from the CSV export.
  description_de?: string;
  is_vegetarian?: boolean;
  is_vegan?: boolean;
  // Pricing fields preserved from Firestore so the CSV export can show
  // base vs. incl.-tax separately (some delivery platforms ask for
  // net prices).
  base_price?: number;
  price_with_tax?: number;
  tax_rate?: number;
  image_url?: string;
}

// Keyword-based brand derivation — must mirror `deriveBrandFromCategory`
// in web-qr-order/src/services/menuService.ts so the admin sees exactly
// what each storefront sees when filtering by brand.
const SAHEEH_CATEGORY_KEYWORDS = [
  'sushi', 'maki', 'nigiri', 'sashimi', 'roll', 'inside', 'poke', 'bowl',
];
const PIZZA_BRAND_CATEGORY_KEYWORDS = [
  'pizza', 'pasta', 'insalata', 'pizza_brand', 'pizza_brand', 'vorspeisen',
];
function deriveBrandFromCategory(categoryName: string): 'saheeh' | 'pizza_brand' | 'both' {
  const n = (categoryName || '').toLowerCase();
  if (!n) return 'both';
  if (SAHEEH_CATEGORY_KEYWORDS.some(k => n.includes(k))) return 'saheeh';
  if (PIZZA_BRAND_CATEGORY_KEYWORDS.some(k => n.includes(k))) return 'pizza_brand';
  return 'both';
}
function effectiveBrand(explicit: string | null | undefined, categoryName: string): 'saheeh' | 'pizza_brand' | 'both' {
  if (explicit === 'saheeh' || explicit === 'pizza_brand' || explicit === 'both') return explicit;
  return deriveBrandFromCategory(categoryName);
}
// Returns the sort value that applies for a given brand. When brandFilter
// is 'all' we fall back to the legacy `display_order`. For a specific brand
// we prefer the per-brand slot, then `display_order`, then a large sentinel.
function pickDisplayOrder(
  item: { display_order?: number; display_order_by_brand?: { saheeh?: number; pizza_brand?: number } | null },
  brand: 'all' | 'saheeh' | 'pizza_brand',
): number {
  if (brand === 'all') {
    return item.display_order ?? 999999;
  }
  const perBrand = item.display_order_by_brand?.[brand];
  if (typeof perBrand === 'number') return perBrand;
  return item.display_order ?? 999999;
}

// ── AdminMenuItem → local MenuItem mapper ─────────────────────────────
// Single source of truth for converting a Firestore menu item to the local
// shape used by the Menu page grid. Uses spread-then-override so every
// field on the source object (description_de, brand, is_vegetarian,
// is_vegan, price_with_tax, modifier_group_ids, etc.) is preserved —
// previously each of the 5 ad-hoc mappings dropped different subsets
// of fields, causing the German-description column in the CSV export
// to always be empty (Owner caught this on 2026-04-24).
function mapAdminItemToLocal(item: AdminMenuItem): MenuItem {
  return {
    ...item,
    id: item.id,
    name: item.name,
    name_de: item.name_de || item.name,
    description: item.description || '',
    // No English→German fallback — keeps the admin grid honest about which
    // items are actually translated. Export/import both depend on this.
    description_de: item.description_de || '',
    category: item.category || '',
    price: item.price_with_tax || item.price || item.base_price || 0,
    available: item.is_available !== false,
    sold_out: item.sold_out === true,
    popular: item.is_popular === true,
    display_order: typeof item.display_order === 'number' ? item.display_order : 999999,
    display_order_by_brand: item.display_order_by_brand || null,
    brand: item.brand || null,
    is_vegetarian: item.is_vegetarian === true,
    is_vegan: item.is_vegan === true,
    image_url: item.image_url || '',
  } as MenuItem;
}

// ── Menu CSV export ─────────────────────────────────────────────────────
// Client requested 2026-04-24: one-click export of all menu items so menus
// can be sent to Lieferando / Uber Eats / Wolt / Excel without manual
// re-typing. Respects the current brand filter so Saheeh-only and Don
// Vito-only exports produce clean per-brand files.
//
// Output is RFC 4180 CSV with a UTF-8 BOM so Excel opens umlauts correctly.
// Phase 2 (bulk import) is planned but not yet shipped — see the
// `pos-order-status-filter-rule` style memory once that lands.

function csvEscape(value: any): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  // RFC 4180: if field contains comma, quote, or newline → wrap in quotes
  // and double any internal quotes.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function handleExportMenuCsv(
  items: any[],
  brandFilter: 'all' | 'saheeh' | 'pizza_brand',
  categories: any[],
): void {
  // Apply brand filter so the export matches what the user is currently viewing.
  const visible = items.filter((item: any) => {
    if (brandFilter === 'all') return true;
    const explicit = item.brand;
    if (explicit === 'saheeh' || explicit === 'pizza_brand' || explicit === 'both') {
      return explicit === brandFilter || explicit === 'both';
    }
    // Fallback: derive brand from category name (same rules as storefront).
    const cat = (item.category || '').toLowerCase();
    const saheehKeywords = ['sushi', 'maki', 'nigiri', 'sashimi', 'roll', 'inside', 'poke', 'bowl'];
    const pizza_brandKeywords = ['pizza', 'pasta', 'insalata', 'pizza_brand', 'pizza_brand', 'vorspeisen'];
    if (saheehKeywords.some(k => cat.includes(k))) return brandFilter === 'saheeh';
    if (pizza_brandKeywords.some(k => cat.includes(k))) return brandFilter === 'pizza_brand';
    return true; // "both" items show in both exports
  });

  // Extract a human-friendly item number from the name prefix if present
  // (e.g. "130 Veggie-Crispy Roll" → "130"). Falls back to empty.
  const extractItemNumber = (name: string): string => {
    const match = (name || '').match(/^(\d+)[\s.:\-—]/);
    return match ? match[1] : '';
  };

  // Determine brand for a given item (for the Brand column).
  const resolveBrand = (item: any): string => {
    const explicit = item.brand;
    if (explicit === 'saheeh') return 'Saheeh';
    if (explicit === 'pizza_brand') return 'Pizza Brand';
    if (explicit === 'both') return 'Both';
    const cat = (item.category || '').toLowerCase();
    const saheehKeywords = ['sushi', 'maki', 'nigiri', 'sashimi', 'roll', 'inside', 'poke', 'bowl'];
    const pizza_brandKeywords = ['pizza', 'pasta', 'insalata', 'pizza_brand', 'pizza_brand', 'vorspeisen'];
    if (saheehKeywords.some(k => cat.includes(k))) return 'Saheeh';
    if (pizza_brandKeywords.some(k => cat.includes(k))) return 'Pizza Brand';
    return 'Both';
  };

  // Column order chosen for delivery-platform imports and human readability.
  const columns = [
    'Item Number', 'Firestore ID', 'Name (EN)', 'Name (DE)',
    'Description (EN)', 'Description (DE)', 'Category', 'Brand',
    'Price (incl. tax)', 'Base Price (excl. tax)', 'Tax Rate %',
    'Available', 'Sold Out', 'Vegetarian', 'Vegan', 'Popular',
    'Image URL',
  ];

  const rows = visible.map((item: any) => {
    const priceInclTax = item.price_with_tax ?? item.price ?? 0;
    const taxRate = item.tax_rate ?? 7;
    const basePrice = item.base_price ?? (taxRate ? priceInclTax / (1 + taxRate / 100) : priceInclTax);
    return [
      extractItemNumber(item.name),
      item.id || '',
      item.name || '',
      item.name_de || item.name || '',
      item.description || '',
      // Raw German description — empty cell = translation missing. Do NOT
      // fall back to English; that would mask translation gaps and defeats
      // the purpose of exporting the menu for delivery platforms.
      item.description_de || '',
      item.category || '',
      resolveBrand(item),
      Number(priceInclTax).toFixed(2),
      Number(basePrice).toFixed(2),
      String(taxRate),
      item.available === false || item.is_available === false ? 'no' : 'yes',
      item.sold_out ? 'yes' : 'no',
      item.is_vegetarian ? 'yes' : 'no',
      item.is_vegan ? 'yes' : 'no',
      item.is_popular || item.popular ? 'yes' : 'no',
      item.image_url || '',
    ];
  });

  // Sort rows by category then name for easier human scanning.
  rows.sort((a, b) => {
    const catCmp = String(a[6]).localeCompare(String(b[6]));
    if (catCmp !== 0) return catCmp;
    return String(a[2]).localeCompare(String(b[2]));
  });

  const csvBody = [columns, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\r\n');

  // UTF-8 BOM (﻿) makes Excel / Windows properly decode umlauts.
  const blob = new Blob(['﻿' + csvBody], { type: 'text/csv;charset=utf-8;' });

  const brandLabel = brandFilter === 'all' ? 'all' : brandFilter === 'saheeh' ? 'saheeh' : 'pizza_brand';
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const filename = `saheeh-menu-${brandLabel}-${dateStr}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  console.log(`📤 [MENU EXPORT] ${filename} (${visible.length} items, brand=${brandFilter})`);
}

// ── Shared dark-dialog primitives (used by the redesigned Menu/Category/Add-On dialogs) ──

interface FormSectionProps {
  icon: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
const FormSection: React.FC<FormSectionProps> = ({ icon, title, subtitle, children }) => (
  <Box sx={{
    mb: 2,
    p: 2,
    backgroundColor: 'rgba(22,25,40,0.5)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 2.5,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: 1.5, backgroundColor: 'rgba(0, 200, 150,0.08)', border: '1px solid rgba(0, 200, 150,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#f0f2f5', lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.2 }}>{subtitle}</Typography>}
      </Box>
    </Box>
    {children}
  </Box>
);

const darkTextFieldSx = {
  '& label': { fontFamily: '"Poppins", sans-serif', color: '#9ca3af', fontSize: '0.85rem' },
  '& label.Mui-focused': { color: '#00C896' },
  '& .MuiOutlinedInput-root': {
    fontFamily: '"Poppins", sans-serif',
    color: '#f0f2f5',
    borderRadius: 2,
    backgroundColor: 'rgba(11,13,24,0.6)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(0, 200, 150,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#00C896' },
  },
  '& .MuiInputAdornment-root p': { color: '#6b7280', fontFamily: '"Poppins", sans-serif' },
};

interface DarkTextFieldProps {
  label: string;
  value: any;
  onChange: (v: string) => void;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  type?: string;
  placeholder?: string;
  inputProps?: any;
  helperText?: string;
  startAdornment?: string;
}
const DarkTextField: React.FC<DarkTextFieldProps> = ({ label, value, onChange, required, multiline, rows, type, placeholder, inputProps, helperText, startAdornment }) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    required={required}
    multiline={multiline}
    rows={rows}
    type={type}
    placeholder={placeholder}
    inputProps={inputProps}
    helperText={helperText}
    size="small"
    InputProps={startAdornment ? { startAdornment: <InputAdornment position="start"><Typography sx={{ color: '#6b7280', fontFamily: '"Poppins", sans-serif' }}>{startAdornment}</Typography></InputAdornment> } : undefined}
    sx={darkTextFieldSx}
    FormHelperTextProps={{ sx: { color: '#6b7280', fontFamily: '"Poppins", sans-serif', fontSize: '0.68rem', ml: 0.5 } }}
  />
);

interface DarkSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  options: Array<{ value: string; label: string }>;
}
const DarkSelect: React.FC<DarkSelectProps> = ({ label, value, onChange, required, options }) => (
  <FormControl fullWidth required={required} size="small" sx={darkTextFieldSx}>
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      onChange={(e) => onChange(String(e.target.value))}
      label={label}
      MenuProps={{ PaperProps: { sx: { backgroundColor: '#111427', color: '#f0f2f5', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, '& .MuiMenuItem-root': { fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem', '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.08)' }, '&.Mui-selected': { backgroundColor: 'rgba(0, 200, 150,0.12)', color: '#00C896', '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.18)' } } } } } }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const MenuPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Brand filter: controls which items/categories are visible AND which
  // sort slot the up/down arrows write to. 'all' writes the legacy
  // `display_order` field. 'saheeh'/'pizza_brand' write to
  // `display_order_by_brand.<brand>` so each storefront can have its own
  // arrangement without affecting the other.
  const [brandFilter, setBrandFilter] = useState<'all' | 'saheeh' | 'pizza_brand'>('all');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // New layout (2026-04-22): category rail + card grid.
  // 'all' shows every category; any other value is a category name match.
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  // Quick-filter chips above the grid.
  const [quickFilter, setQuickFilter] = useState<'all' | 'noImage' | 'soldOut' | 'unavailable'>('all');
  // Bulk select — set of item ids currently checked.
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  // Import menu dialog (Phase 2 of the CSV export/import feature)
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  // Full AdminMenuItem shape (not just the local MenuItem) needed for the
  // diff engine. Loaded lazily when the Import dialog opens.
  const [adminMenuItemsForImport, setAdminMenuItemsForImport] = useState<AdminMenuItem[]>([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MenuItem | null>(null);
  
  // Category management states
  const [categoryListDialogOpen, setCategoryListDialogOpen] = useState(false);
  const [categoryEditDialogOpen, setCategoryEditDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AdminCategory | null>(null);
  const [pendingImageUpload, setPendingImageUpload] = useState<{ url: string; categoryId: string } | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    icon: '🍽️',
    image_url: '',
    is_active: true,
    display_order: 0,
  });
  const [categoryDeleteDialogOpen, setCategoryDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<AdminCategory | null>(null);
  
  // Add-ons management states
  const [addOnsListDialogOpen, setAddOnsListDialogOpen] = useState(false);
  const [addOns, setAddOns] = useState<AdminAddOn[]>([]);
  const [addOnEditDialogOpen, setAddOnEditDialogOpen] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState<AdminAddOn | null>(null);
  const [addOnDeleteDialogOpen, setAddOnDeleteDialogOpen] = useState(false);
  const [addOnToDelete, setAddOnToDelete] = useState<AdminAddOn | null>(null);
  const [addOnFormData, setAddOnFormData] = useState({
    name: '',
    name_de: '',
    price: '',
    description: '',
    is_active: true,
    sort_order: 0,
  });
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    name_de: '',
    description: '',
    description_de: '',
    base_price: '',
    tax_rate: '7',
    category: '',
    image_url: '',
    is_available: true,
    is_vegetarian: false,
    is_vegan: false,
    is_popular: false,
    // 'auto' means: no explicit value — QR app will derive the brand from the
    // category name (saheeh keywords, pizza/pasta → pizza_brand, else → both).
    brand: 'auto' as 'auto' | 'saheeh' | 'pizza_brand' | 'both',
    modifier_group_ids: [] as string[],
  });

  const [availableModifierGroups, setAvailableModifierGroups] = useState<AdminModifierGroup[]>([]);

  // Check authentication on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('✅ [MENU PAGE] User authenticated:', user.email);
        setIsAuthenticated(true);
      } else {
        console.log('⚠️ [MENU PAGE] User not authenticated, redirecting to login...');
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch categories and menu items from Firestore
  useEffect(() => {
    // Only fetch data if user is authenticated
    if (isAuthenticated === false) {
      return; // Don't fetch if not authenticated
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 [MENU PAGE] Fetching data from Firestore...');
        
        // Fetch categories and menu items in parallel
        const [categoriesData, itemsData] = await Promise.all([
          firebaseAdminService.getCategories(),
          firebaseAdminService.getMenuItems()
        ]);
        
        console.log('✅ [MENU PAGE] Loaded:', {
          categories: categoriesData.length,
          items: itemsData.length
        });
        
        // Set categories
        setCategories(categoriesData);
        
        // Map menu items — uses shared mapper so description_de, brand,
        // is_vegetarian, is_vegan etc. are all preserved.
        const fixedItems: MenuItem[] = itemsData.map(mapAdminItemToLocal);
        setMenuItems(fixedItems);
      } catch (error) {
        console.error('❌ [MENU PAGE] Failed to fetch data:', error);
        setError('Failed to load menu items. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (isAuthenticated === true) {
      fetchData();
      // Load modifier groups for the item editor
      firebaseAdminService.getModifierGroups().then(setAvailableModifierGroups).catch(() => {});
    }
  }, [isAuthenticated]);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Open dialog for adding new item
  const handleAddItem = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      name_de: '',
      description: '',
      description_de: '',
      base_price: '',
      tax_rate: '7',
      category: '',
      image_url: '',
      is_available: true,
      is_vegetarian: false,
      is_vegan: false,
      is_popular: false,
      brand: 'auto',
      modifier_group_ids: [],
    });
    setDialogOpen(true);
  };

  // Open dialog for editing item
  const handleEditItem = async (item: MenuItem) => {
    setEditingItem(item);
    // Fetch full item data from Firebase to get tax_rate and other fields
    try {
      const fullItem = await firebaseAdminService.getMenuItemById(item.id.toString());
      const imageUrl = (fullItem as any)?.image_url || '';
      console.log('📸 [MENU ITEM] Loading item for edit, image_url:', imageUrl);
      
      // When editing, show the FINAL PRICE (price_with_tax) in the form, not base_price
      // This way user sees what they'll get, not the calculated base price
      const finalPrice = (fullItem as any)?.price_with_tax || (fullItem as any)?.price || item.price || (fullItem as any)?.base_price || '';
      
      console.log('📝 [MENU ITEM] Loading for edit:', {
        item_price: item.price,
        fullItem_price_with_tax: (fullItem as any)?.price_with_tax,
        fullItem_price: (fullItem as any)?.price,
        fullItem_base_price: (fullItem as any)?.base_price,
        finalPrice_shown_in_form: finalPrice
      });
      
      const rawBrand = (fullItem as any)?.brand;
      const brandValue: 'auto' | 'saheeh' | 'pizza_brand' | 'both' =
        rawBrand === 'saheeh' || rawBrand === 'pizza_brand' || rawBrand === 'both' ? rawBrand : 'auto';
      setFormData({
        name: item.name,
        name_de: item.name_de || item.name || '',
        description: item.description || '',
        // Show the real German description (empty if missing) so the admin
        // can clearly see when translation is needed — previous fallback
        // pre-filled the field with English, making it look translated.
        description_de: (fullItem as any)?.description_de || '',
        base_price: finalPrice.toString(), // Show final price (price_with_tax) in the form
        tax_rate: (fullItem as any)?.tax_rate?.toString() || '7',
        category: item.category || '',
        image_url: imageUrl,
        is_available: item.available,
        is_vegetarian: (fullItem as any)?.is_vegetarian || false,
        is_vegan: (fullItem as any)?.is_vegan || false,
        is_popular: (fullItem as any)?.is_popular || false,
        brand: brandValue,
        modifier_group_ids: Array.isArray((fullItem as any)?.modifier_group_ids) ? (fullItem as any).modifier_group_ids : [],
      });
      console.log('📸 [MENU ITEM] Form data set, image_url:', imageUrl);
    } catch (error) {
      // Fallback to basic data
      setFormData({
        name: item.name,
        name_de: item.name_de || item.name || '',
        description: item.description || '',
        description_de: '',
        base_price: '',
        tax_rate: '7',
        category: item.category || '',
        image_url: '',
        is_available: item.available,
        is_vegetarian: false,
        is_vegan: false,
        is_popular: false,
        brand: 'auto',
        modifier_group_ids: [],
      });
    }
    setDialogOpen(true);
  };

  // Handle save (create or update)
  const handleSave = async () => {
    try {
      setError(null);

      // User enters FINAL PRICE (with tax included) - calculate base_price from it
      const finalPrice = parseFloat(formData.base_price); // This is actually the final price the user wants
      if (isNaN(finalPrice) || finalPrice <= 0) {
        setError('Please enter a valid price greater than 0');
        return;
      }
      
      const taxRate = parseFloat(formData.tax_rate);
      if (isNaN(taxRate) || taxRate < 0 || taxRate > 100) {
        setError('Please enter a valid tax rate between 0 and 100');
        return;
      }

      // Calculate base_price from final price (price_with_tax)
      // Formula: base_price = price_with_tax / (1 + tax_rate / 100)
      // Example: If user enters 9.90 with 7% tax:
      //   base_price = 9.90 / 1.07 = 9.2523... ≈ 9.25
      //   price_with_tax = 9.90 (what user entered - we'll use this directly)
      const basePrice = finalPrice / (1 + taxRate / 100);

      // Auto-mirror EN <-> DE when the client only filled one side. This keeps
      // both storefront languages readable instead of showing blank names to
      // customers whose browser is set to the empty locale.
      const nameEn = (formData.name || '').trim();
      const nameDe = (formData.name_de || '').trim();
      const descEn = (formData.description || '').trim();
      const descDe = (formData.description_de || '').trim();
      if (!nameEn && !nameDe) {
        setError('Please enter the item name in English or German');
        return;
      }
      const itemData: Partial<AdminMenuItem> = {
        name: nameEn || nameDe,
        name_de: nameDe || nameEn,
        description: descEn || descDe,
        description_de: descDe || descEn,
        base_price: basePrice,
        tax_rate: taxRate,
        // IMPORTANT: Set price_with_tax to the user-entered final price
        // This ensures the displayed price matches exactly what user entered
        price_with_tax: finalPrice,
        price: finalPrice, // Also set price for compatibility
        category: formData.category,
        image_url: formData.image_url || '',
        is_available: formData.is_available,
        is_vegetarian: formData.is_vegetarian,
        is_vegan: formData.is_vegan,
        is_popular: formData.is_popular,
        modifier_group_ids: formData.modifier_group_ids || [],
      };
      // Only write an explicit `brand` when admin picked one. 'auto' leaves the
      // field empty so the QR app falls back to category-name derivation.
      if (formData.brand !== 'auto') {
        (itemData as any).brand = formData.brand;
      } else if (editingItem) {
        // Admin switched back to auto — clear any previously saved brand.
        (itemData as any).brand = null;
      }
      
      console.log('💾 [MENU ITEM] Saving item:', {
        name: itemData.name,
        user_entered_final_price: finalPrice,
        calculated_base_price: basePrice.toFixed(4),
        tax_rate: taxRate,
        price_with_tax: finalPrice,
        formula: `base_price = ${finalPrice} / (1 + ${taxRate} / 100) = ${basePrice.toFixed(4)}`,
        verification: `base_price * (1 + ${taxRate}%) = ${basePrice.toFixed(4)} * 1.${taxRate} = ${(basePrice * (1 + taxRate / 100)).toFixed(2)}`,
        image_url: itemData.image_url || 'EMPTY'
      });

      if (editingItem) {
        console.log('💾 [MENU PAGE] Updating menu item:', {
          itemId: editingItem.id.toString(),
          itemData: itemData,
          price_with_tax: itemData.price_with_tax,
          price: itemData.price,
          base_price: itemData.base_price
        });
        await firebaseAdminService.updateMenuItem(editingItem.id.toString(), itemData);
        console.log('✅ [MENU PAGE] Update call completed');
      } else {
        console.log('💾 [MENU PAGE] Creating new menu item:', itemData);
        await firebaseAdminService.createMenuItem(itemData);
        console.log('✅ [MENU PAGE] Create call completed');
      }

      setDialogOpen(false);
      
      // Wait a moment for Firestore to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh menu items
      console.log('🔄 [MENU PAGE] Refreshing menu items...');
      const [categoriesData, itemsData] = await Promise.all([
        firebaseAdminService.getCategories(),
        firebaseAdminService.getMenuItems()
      ]);
      
      setCategories(categoriesData);
      const fixedItems: MenuItem[] = itemsData.map((item: AdminMenuItem) => {
        const displayPrice = item.price_with_tax || item.price || item.base_price || 0;
        // Log the updated item if it matches the one we just edited
        if (editingItem && (item.id === editingItem.id || item.name === editingItem.name)) {
          const expectedPrice = finalPrice; // finalPrice from handleSave scope
          console.log('🔄 [MENU PAGE] Updated item after save:', {
            id: item.id,
            name: item.name,
            base_price: item.base_price,
            tax_rate: item.tax_rate,
            price_with_tax: item.price_with_tax,
            price: item.price,
            displayPrice: displayPrice,
            expectedPrice: expectedPrice,
            matches_expected: Math.abs(displayPrice - expectedPrice) < 0.01
          });
        }
        // Use shared mapper to preserve description_de, brand, etc.
        return mapAdminItemToLocal(item);
      });
      setMenuItems(fixedItems);
      console.log('✅ [MENU PAGE] Menu items refreshed, total items:', fixedItems.length);
      
      // Clear editing state
      setEditingItem(null);
    } catch (error) {
      console.error('❌ [MENU PAGE] Save error:', error);
      setError(`Failed to ${editingItem ? 'update' : 'create'} menu item. Please try again.`);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (item: MenuItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await firebaseAdminService.deleteMenuItem(itemToDelete.id.toString());
      
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      
      // Refresh menu items
      const [categoriesData, itemsData] = await Promise.all([
        firebaseAdminService.getCategories(),
        firebaseAdminService.getMenuItems()
      ]);
      
      setCategories(categoriesData);
      const fixedItems: MenuItem[] = itemsData.map(mapAdminItemToLocal);
      setMenuItems(fixedItems);
    } catch (error) {
      console.error('❌ [MENU PAGE] Delete error:', error);
      setError('Failed to delete menu item. Please try again.');
    }
  };

  // Inline toggle — flip the sold_out flag on a single item.
  // Optimistic local update, then Firestore write. Reverts on error.
  const handleToggleSoldOut = async (item: MenuItem) => {
    const next = !item.sold_out;
    setMenuItems(prev => prev.map(m => m.id === item.id ? { ...m, sold_out: next } : m));
    try {
      await firebaseAdminService.updateMenuItem(item.id.toString(), {
        sold_out: next,
        sold_out_at: next ? new Date().toISOString() : '',
        sold_out_by: next ? (auth.currentUser?.email || '') : '',
      } as Partial<AdminMenuItem>);
    } catch (err) {
      console.error('❌ [MENU PAGE] Toggle sold_out failed:', err);
      setMenuItems(prev => prev.map(m => m.id === item.id ? { ...m, sold_out: item.sold_out } : m));
      setError('Failed to update item. Please try again.');
    }
  };

  // Reorder: swap display_order with the adjacent item in the currently visible list.
  // When brandFilter is 'all' we update the legacy `display_order` field (shared
  // across both brands + POS). When it's 'saheeh' or 'pizza_brand' we write to
  // `display_order_by_brand.<brand>` so only that storefront is affected.
  const handleMoveItem = async (itemId: string | number, direction: 'up' | 'down', visibleList: MenuItem[]) => {
    const idx = visibleList.findIndex(m => m.id === itemId);
    if (idx < 0) return;
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= visibleList.length) return;
    const current = visibleList[idx];
    const neighbor = visibleList[neighborIdx];

    // Normalize display_order values — many existing items default to 999999 (unsorted).
    // Assign deterministic values based on current list order so swaps always produce
    // a visible move rather than a no-op on two-of-999999.
    const currentOrder = pickDisplayOrder(current, brandFilter) ?? idx * 10;
    const neighborOrder = pickDisplayOrder(neighbor, brandFilter) ?? neighborIdx * 10;
    const nextCurrent = neighborOrder;
    const nextNeighbor = currentOrder === neighborOrder ? currentOrder - (direction === 'up' ? 1 : -1) : currentOrder;

    // Build the per-brand-aware payload. Preserve any pre-existing slot we are
    // not editing so a Saheeh move doesn't erase Pizza Brand's value (and vice versa).
    // Firestore rejects `undefined` values in objects, so only include slots
    // whose value is an actual number.
    const patchFor = (item: MenuItem, nextValue: number): Partial<AdminMenuItem> => {
      if (brandFilter === 'all') {
        return { display_order: nextValue };
      }
      const existing = item.display_order_by_brand || {};
      const merged: { saheeh?: number; pizza_brand?: number } = {};
      if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
      if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
      merged[brandFilter] = nextValue;
      return { display_order_by_brand: merged };
    };

    // Optimistic update
    setMenuItems(prev => prev.map(m => {
      if (m.id === current.id) {
        if (brandFilter === 'all') return { ...m, display_order: nextCurrent };
        return { ...m, display_order_by_brand: { ...(m.display_order_by_brand || {}), [brandFilter]: nextCurrent } };
      }
      if (m.id === neighbor.id) {
        if (brandFilter === 'all') return { ...m, display_order: nextNeighbor };
        return { ...m, display_order_by_brand: { ...(m.display_order_by_brand || {}), [brandFilter]: nextNeighbor } };
      }
      return m;
    }));

    try {
      await Promise.all([
        firebaseAdminService.updateMenuItem(String(current.id), patchFor(current, nextCurrent)),
        firebaseAdminService.updateMenuItem(String(neighbor.id), patchFor(neighbor, nextNeighbor)),
      ]);
    } catch (err) {
      console.error('❌ [MENU PAGE] Reorder failed:', err);
      // Revert
      setMenuItems(prev => prev.map(m => {
        if (m.id === current.id) {
          if (brandFilter === 'all') return { ...m, display_order: current.display_order };
          return { ...m, display_order_by_brand: current.display_order_by_brand };
        }
        if (m.id === neighbor.id) {
          if (brandFilter === 'all') return { ...m, display_order: neighbor.display_order };
          return { ...m, display_order_by_brand: neighbor.display_order_by_brand };
        }
        return m;
      }));
      setError('Failed to reorder. Please try again.');
    }
  };

  // Returns the sort value to use when ordering categories under the current
  // brand filter. Mirrors pickDisplayOrder() but tolerates AdminCategory shape.
  const pickCategoryOrder = (c: any, brand: 'all' | 'saheeh' | 'pizza_brand'): number => {
    if (brand === 'all') return c.display_order ?? 999999;
    const perBrand = c.display_order_by_brand?.[brand];
    if (typeof perBrand === 'number') return perBrand;
    return c.display_order ?? 999999;
  };

  // Which categories should be visible under the current brand filter —
  // a category is "in" a brand if it has any menu item belonging to that brand
  // (or to 'both'). 'all' shows every category.
  const visibleCategoriesForBrand = (all: any[], brand: 'all' | 'saheeh' | 'pizza_brand') => {
    if (brand === 'all') return all;
    return all.filter(cat => {
      return menuItems.some(item => {
        if ((item.category || '').toLowerCase() !== (cat.name || '').toLowerCase()) return false;
        const b = effectiveBrand(item.brand, item.category || '');
        return b === brand || b === 'both';
      });
    });
  };

  const handleMoveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    // Work against only the categories that belong to the current brand.
    // That way reordering in "Pizza Brand" mode never shuffles Saheeh-only
    // categories around in Saheeh's sidebar.
    const pool = visibleCategoriesForBrand(categories, brandFilter);
    const sorted = [...pool].sort((a, b) => {
      const diff = pickCategoryOrder(a, brandFilter) - pickCategoryOrder(b, brandFilter);
      return diff !== 0 ? diff : (a.name || '').localeCompare(b.name || '');
    });
    const idx = sorted.findIndex(c => c.id === categoryId);
    if (idx < 0) return;
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= sorted.length) return;

    // Swap current ↔ neighbor in the sorted list.
    const swapped = [...sorted];
    [swapped[idx], swapped[neighborIdx]] = [swapped[neighborIdx], swapped[idx]];

    // Assign strictly increasing orders (10, 20, 30, ...) so Firestore always
    // stores unique values — even when categories were auto-extracted and all
    // had display_order=0.
    const newOrders = swapped.map((c, i) => ({ id: c.id, order: (i + 1) * 10 }));
    const orderMap = new Map(newOrders.map(o => [o.id, o.order]));

    const previousCategories = [...categories];

    // Optimistic UI update: write to the correct slot based on brandFilter.
    setCategories(prev => prev.map(c => {
      const next = orderMap.get(c.id);
      if (next === undefined) return c;
      if (brandFilter === 'all') {
        return { ...c, display_order: next };
      }
      return {
        ...c,
        display_order_by_brand: { ...(c.display_order_by_brand || {}), [brandFilter]: next },
      };
    }));

    try {
      const writes = newOrders.map(({ id, order }) => {
        const src = previousCategories.find(c => c.id === id);
        // Always include name/icon/image_url/is_active so that auto-extracted
        // categories get materialized into Firestore as real docs (not blank)
        // — otherwise the next refresh re-creates them with display_order=0
        // and the drag order gets lost.
        const meta = {
          name: src?.name,
          icon: src?.icon,
          image_url: src?.image_url,
          is_active: src?.is_active !== false,
        };
        if (brandFilter === 'all') {
          return firebaseAdminService.updateCategory(id, { ...meta, display_order: order });
        }
        const existing = src?.display_order_by_brand || {};
        const merged: { saheeh?: number; pizza_brand?: number } = {};
        if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
        if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
        merged[brandFilter] = order;
        return firebaseAdminService.updateCategory(id, {
          ...meta,
          display_order_by_brand: merged,
        });
      });
      await Promise.all(writes);
      console.log(`✅ [MENU PAGE] Reordered ${writes.length} categories (brand=${brandFilter})`);
      // Refresh so auto-extracted categories get real Firestore docs.
      const fresh = await firebaseAdminService.getCategories();
      setCategories(fresh);
    } catch (err) {
      console.error('❌ [MENU PAGE] Reorder category failed:', err);
      setCategories(previousCategories);
      setError('Failed to reorder category. Please try again.');
    }
  };

  // Drag-and-drop helper for categories. Takes the full ordered list of
  // category ids (after drop) and writes strictly-increasing display_order
  // values (10, 20, 30, ...) into whichever slot the current brandFilter
  // points at — same semantics as `handleMoveCategory` but supports an
  // arbitrary drop destination rather than single-step swaps.
  const reorderCategoriesByIds = async (orderedIds: string[]) => {
    const newOrders = orderedIds.map((id, i) => ({ id, order: (i + 1) * 10 }));
    const orderMap = new Map(newOrders.map(o => [o.id, o.order]));
    const previousCategories = [...categories];

    // Optimistic UI update
    setCategories(prev => prev.map(c => {
      const next = orderMap.get(c.id);
      if (next === undefined) return c;
      if (brandFilter === 'all') return { ...c, display_order: next };
      const existing = c.display_order_by_brand || {};
      const merged: { saheeh?: number; pizza_brand?: number } = {};
      if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
      if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
      merged[brandFilter] = next;
      return { ...c, display_order_by_brand: merged };
    }));

    try {
      const writes = newOrders.map(({ id, order }) => {
        const src = previousCategories.find(c => c.id === id);
        // Always include name/icon/image_url/is_active so that auto-extracted
        // categories get materialized into Firestore as real docs (not blank)
        // — otherwise the next refresh re-creates them with display_order=0
        // and the drag order snaps back.
        const meta = {
          name: src?.name,
          icon: src?.icon,
          image_url: src?.image_url,
          is_active: src?.is_active !== false,
        };
        if (brandFilter === 'all') {
          return firebaseAdminService.updateCategory(id, { ...meta, display_order: order });
        }
        const existing = src?.display_order_by_brand || {};
        const merged: { saheeh?: number; pizza_brand?: number } = {};
        if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
        if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
        merged[brandFilter] = order;
        return firebaseAdminService.updateCategory(id, { ...meta, display_order_by_brand: merged });
      });
      await Promise.all(writes);
      const fresh = await firebaseAdminService.getCategories();
      setCategories(fresh);
    } catch (err) {
      console.error('❌ [MENU PAGE] Drag reorder failed:', err);
      setCategories(previousCategories);
      setError('Failed to reorder category. Please try again.');
    }
  };

  // Drag-and-drop helper for items in the visible list.
  const reorderItemsByIds = async (orderedIds: Array<string | number>, visibleList: MenuItem[]) => {
    const newOrders = orderedIds.map((id, i) => ({ id, order: (i + 1) * 10 }));
    const orderMap = new Map(newOrders.map(o => [String(o.id), o.order]));
    const previousItems = [...menuItems];

    // Optimistic UI update
    setMenuItems(prev => prev.map(m => {
      const next = orderMap.get(String(m.id));
      if (next === undefined) return m;
      if (brandFilter === 'all') return { ...m, display_order: next };
      const existing = m.display_order_by_brand || {};
      const merged: { saheeh?: number; pizza_brand?: number } = {};
      if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
      if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
      merged[brandFilter] = next;
      return { ...m, display_order_by_brand: merged };
    }));

    try {
      const writes = newOrders.map(({ id, order }) => {
        if (brandFilter === 'all') {
          return firebaseAdminService.updateMenuItem(String(id), { display_order: order } as Partial<AdminMenuItem>);
        }
        const src = visibleList.find(v => String(v.id) === String(id));
        const existing = src?.display_order_by_brand || {};
        const merged: { saheeh?: number; pizza_brand?: number } = {};
        if (typeof existing.saheeh === 'number') merged.saheeh = existing.saheeh;
        if (typeof existing.pizza_brand === 'number') merged.pizza_brand = existing.pizza_brand;
        merged[brandFilter] = order;
        return firebaseAdminService.updateMenuItem(String(id), { display_order_by_brand: merged } as Partial<AdminMenuItem>);
      });
      await Promise.all(writes);
    } catch (err) {
      console.error('❌ [MENU PAGE] Drag reorder items failed:', err);
      setMenuItems(previousItems);
      setError('Failed to reorder items. Please try again.');
    }
  };

  const formatPrice = (price: number | string | undefined) => {
    if (price === null || price === undefined) return '€0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '€0.00';
    return `€${numPrice.toFixed(2)}`;
  };

  // Filter menu items based on search + brand, then sort.
  // When brandFilter is 'saheeh' or 'pizza_brand' we hide items tagged to the
  // other brand (items tagged 'both' stay visible for both), and we sort
  // using that brand's per-brand display_order slot.
  // Items that pass brand + search (used by the category rail so each row's
  // count reflects the current brand/search context, not the global menu).
  const brandSearchItems = menuItems.filter((item: MenuItem) => {
    if (brandFilter !== 'all') {
      const brand = effectiveBrand(item.brand, item.category || '');
      if (brand !== brandFilter && brand !== 'both') return false;
    }
    if (!searchQuery.trim()) return true;
    const words = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    const nameEn = item.name.toLowerCase();
    const nameDe = (item.name_de || '').toLowerCase();
    const cat = (item.category || '').toLowerCase();
    return words.every(w => nameEn.includes(w) || nameDe.includes(w) || cat.includes(w));
  });

  const filteredMenuItems = brandSearchItems
    .filter((item: MenuItem) => {
      if (selectedCategory !== 'all') {
        if ((item.category || '').toLowerCase() !== selectedCategory.toLowerCase()) return false;
      }
      if (quickFilter === 'noImage' && item.image_url && item.image_url.trim() !== '') return false;
      if (quickFilter === 'soldOut' && !item.sold_out) return false;
      if (quickFilter === 'unavailable' && item.available) return false;
      return true;
    })
    .sort((a, b) => {
      const catCompare = (a.category || '').localeCompare(b.category || '');
      if (catCompare !== 0) return catCompare;
      const da = pickDisplayOrder(a, brandFilter);
      const db = pickDisplayOrder(b, brandFilter);
      if (da !== db) return da - db;
      return a.name.localeCompare(b.name);
    });

  // Category rail entries: visible categories for the current brand, with
  // item counts drawn from brand+search filtered items so they update live.
  const categoryRailEntries: Array<{ name: string; icon: string; image_url?: string; count: number; brand: 'saheeh' | 'pizza_brand' | 'both' }> = visibleCategoriesForBrand(categories, brandFilter)
    .slice()
    .sort((a: any, b: any) => {
      const diff = pickCategoryOrder(a, brandFilter) - pickCategoryOrder(b, brandFilter);
      return diff !== 0 ? diff : (a.name || '').localeCompare(b.name || '');
    })
    .map((c: any) => ({
      name: c.name,
      icon: c.icon || '🍽️',
      image_url: c.image_url,
      count: brandSearchItems.filter(it => (it.category || '').toLowerCase() === (c.name || '').toLowerCase()).length,
      brand: deriveBrandFromCategory(c.name),
    }));

  // Calculate statistics for status cards
  const totalMenuItems = menuItems.length;
  const totalCategories = categories.length;
  const availableItems = menuItems.filter(item => item.available).length;
  const unavailableItems = menuItems.filter(item => !item.available).length;
  const availabilityPercentage = totalMenuItems > 0
    ? Math.round((availableItems / totalMenuItems) * 100)
    : 0;
  const itemsWithImages = menuItems.filter(item => item.image_url && item.image_url.trim() !== '').length;
  const imagePercentage = totalMenuItems > 0
    ? Math.round((itemsWithImages / totalMenuItems) * 100)
    : 0;

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (isAuthenticated === false) {
    return null;
  }

  const selectedCount = selectedItemIds.size;
  const toggleItemSelection = (id: string | number) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearSelection = () => setSelectedItemIds(new Set());
  const selectAllVisible = () => setSelectedItemIds(new Set(filteredMenuItems.map(i => String(i.id))));
  const bulkToggleAvailable = async (available: boolean) => {
    const ids = Array.from(selectedItemIds);
    try {
      await Promise.all(ids.map(id => firebaseAdminService.updateMenuItem(id, { is_available: available } as any)));
      setMenuItems(prev => prev.map(m => selectedItemIds.has(String(m.id)) ? { ...m, available } : m));
      clearSelection();
    } catch (e) {
      console.error('Bulk toggle failed:', e);
      setError('Bulk update failed. Please try again.');
    }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedItemIds.size} item(s)? This cannot be undone.`)) return;
    const ids = Array.from(selectedItemIds);
    try {
      await Promise.all(ids.map(id => firebaseAdminService.deleteMenuItem(id)));
      setMenuItems(prev => prev.filter(m => !selectedItemIds.has(String(m.id))));
      clearSelection();
    } catch (e) {
      console.error('Bulk delete failed:', e);
      setError('Bulk delete failed. Please try again.');
    }
  };

  return (
    <AdminLayout>
      <Box sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 1.5, md: 2.5 },
        height: { xs: 'auto', md: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #0b0d18 0%, #0d0f1a 100%)',
      }}>
        {/* ── Header row: title + primary actions ─────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2, flexShrink: 0 }}>
          <Box>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: { xs: '1.4rem', md: '1.75rem' }, fontWeight: 700, color: '#f0f2f5', letterSpacing: '-0.5px' }}>
              {t('menu.title')}
            </Typography>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', color: '#6b7280', mt: 0.25 }}>
              {totalMenuItems} items · {totalCategories} categories · {imagePercentage}% with images
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Category sx={{ fontSize: 18 }} />}
              onClick={() => setCategoryListDialogOpen(true)}
              sx={{
                borderColor: 'rgba(0, 200, 150,0.35)',
                color: '#00C896',
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                px: 1.8,
                '&:hover': { borderColor: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.08)' },
              }}
            >
              {t('menu.manageCategories')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add sx={{ fontSize: 18 }} />}
              onClick={async () => {
                setAddOnsListDialogOpen(true);
                try {
                  const addOnsData = await firebaseAdminService.getAddOns();
                  setAddOns(addOnsData);
                } catch (error) {
                  console.error('Failed to load add-ons:', error);
                  setError('Failed to load add-ons. Please try again.');
                }
              }}
              sx={{
                borderColor: 'rgba(156,39,176,0.45)',
                color: '#c084fc',
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                px: 1.8,
                '&:hover': { borderColor: '#c084fc', backgroundColor: 'rgba(156,39,176,0.1)' },
              }}
            >
              {t('menu.manageAddons')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownload sx={{ fontSize: 18 }} />}
              onClick={() => handleExportMenuCsv(menuItems, brandFilter, categories)}
              sx={{
                borderColor: 'rgba(96,165,250,0.45)',
                color: '#60a5fa',
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                px: 1.8,
                '&:hover': { borderColor: '#60a5fa', backgroundColor: 'rgba(96,165,250,0.1)' },
              }}
              title="Export menu items to CSV (for Lieferando, Uber Eats, Wolt, Excel, etc.)"
            >
              Export Menu
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileUpload sx={{ fontSize: 18 }} />}
              onClick={async () => {
                try {
                  const fresh = await firebaseAdminService.getMenuItems();
                  setAdminMenuItemsForImport(fresh);
                } catch (e) {
                  console.error('Failed to reload menu items for import:', e);
                  setAdminMenuItemsForImport([]);
                }
                setImportDialogOpen(true);
              }}
              sx={{
                borderColor: 'rgba(251,191,36,0.45)',
                color: '#fbbf24',
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                px: 1.8,
                '&:hover': { borderColor: '#fbbf24', backgroundColor: 'rgba(251,191,36,0.1)' },
              }}
              title="Import menu items from CSV (with dry-run preview and auto-backup)"
            >
              Import Menu
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add sx={{ fontSize: 18 }} />}
              onClick={handleAddItem}
              sx={{
                backgroundColor: '#00C896',
                color: '#0d0f1a',
                textTransform: 'none',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: 2,
                px: 2.2,
                boxShadow: '0 4px 14px rgba(0, 200, 150,0.28)',
                '&:hover': { backgroundColor: '#7bc912', boxShadow: '0 6px 20px rgba(0, 200, 150,0.38)' },
              }}
            >
              {t('menu.addMenuItem')}
            </Button>
          </Box>
        </Box>

        {/* ── Sticky filter bar ───────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          p: 1.2,
          mb: 2,
          flexShrink: 0,
          backgroundColor: 'rgba(22,25,40,0.7)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 2.5,
          backdropFilter: 'blur(8px)',
        }}>
          {/* Brand pills */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, backgroundColor: 'rgba(11,13,24,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 0.35 }}>
            {([
              { key: 'all', label: 'POS' },
              { key: 'saheeh', label: 'Saheeh' },
              { key: 'pizza_brand', label: 'Pizza Brand' },
            ] as const).map(opt => {
              const active = brandFilter === opt.key;
              return (
                <Box
                  key={opt.key}
                  onClick={() => { setBrandFilter(opt.key as any); setSelectedCategory('all'); }}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    px: 1.5,
                    py: 0.55,
                    borderRadius: 1.5,
                    fontSize: '0.76rem',
                    fontWeight: 600,
                    fontFamily: '"Poppins", sans-serif',
                    color: active ? '#0d0f1a' : '#9ca3af',
                    backgroundColor: active ? '#00C896' : 'transparent',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: active ? '#00C896' : 'rgba(0, 200, 150,0.08)' },
                  }}
                >
                  <BrandMark brand={opt.key} active={active} size={18} />
                  {opt.label}
                </Box>
              );
            })}
          </Box>

          {/* Search */}
          <TextField
            placeholder={t('menu.searchMenu')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#6b7280', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 280px' },
              maxWidth: { sm: 360 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(11,13,24,0.6)',
                color: '#f0f2f5',
                fontSize: '0.85rem',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                '&:hover fieldset': { borderColor: 'rgba(0, 200, 150,0.25)' },
                '&.Mui-focused fieldset': { borderColor: '#00C896' },
              },
            }}
          />

          {/* Quick filter chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {([
              { key: 'all', label: 'All', color: '#00C896' },
              { key: 'noImage', label: '📷 No image', color: '#f59e0b' },
              { key: 'soldOut', label: '⏸ Sold out', color: '#f97316' },
              { key: 'unavailable', label: '⚠ Hidden', color: '#ef4444' },
            ] as const).map(opt => (
              <Chip
                key={opt.key}
                label={opt.label}
                onClick={() => setQuickFilter(opt.key as any)}
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  height: 26,
                  border: '1px solid',
                  borderColor: quickFilter === opt.key ? opt.color : 'rgba(255,255,255,0.06)',
                  backgroundColor: quickFilter === opt.key ? `${opt.color}22` : 'rgba(11,13,24,0.6)',
                  color: quickFilter === opt.key ? opt.color : '#9ca3af',
                  '&:hover': { backgroundColor: `${opt.color}22`, color: opt.color },
                }}
              />
            ))}
          </Box>

          {/* KPIs (push right) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.75, marginLeft: 'auto' }}>
            <Chip size="small" icon={<CheckCircle sx={{ fontSize: 14, color: '#4CAF50 !important' }} />} label={`${availableItems} available`} sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 600, height: 26, backgroundColor: 'rgba(76,175,80,0.1)', color: '#4CAF50' }} />
            {unavailableItems > 0 && <Chip size="small" icon={<Cancel sx={{ fontSize: 14, color: '#ef4444 !important' }} />} label={`${unavailableItems} hidden`} sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 600, height: 26, backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }} />}
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#00C896' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* ── Main split: category rail (L) + item grid (R) ───────────────── */}
        {!loading && !error && (
          <Box sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            minHeight: 0,
            flexDirection: { xs: 'column', md: 'row' },
          }}>
            {/* ── Category rail ─────────────────────────────────────────── */}
            <Box sx={{
              width: { xs: '100%', md: 260 },
              flexShrink: 0,
              backgroundColor: 'rgba(22,25,40,0.7)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 2.5,
              p: 1,
              overflowY: { xs: 'visible', md: 'auto' },
              maxHeight: { xs: 220, md: 'none' },
              backdropFilter: 'blur(8px)',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 3 },
            }}>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1.2px', px: 1.2, pt: 0.8, pb: 1 }}>
                Categories
              </Typography>
              {/* "All" row */}
              <Box
                onClick={() => setSelectedCategory('all')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.2,
                  py: 1,
                  mb: 0.4,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  backgroundColor: selectedCategory === 'all' ? 'rgba(0, 200, 150,0.12)' : 'transparent',
                  border: '1px solid',
                  borderColor: selectedCategory === 'all' ? 'rgba(0, 200, 150,0.3)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.06)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  {brandFilter === 'all' ? (
                    <BrandMark brand="all" size={20} />
                  ) : brandFilter === 'saheeh' ? (
                    <BrandMark brand="saheeh" size={20} />
                  ) : (
                    <BrandMark brand="pizza_brand" size={20} />
                  )}
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem', fontWeight: selectedCategory === 'all' ? 700 : 500, color: selectedCategory === 'all' ? '#00C896' : '#f0f2f5' }}>
                    {brandFilter === 'all' ? 'All POS items' : brandFilter === 'saheeh' ? 'All Saheeh items' : 'All Pizza Brand items'}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: selectedCategory === 'all' ? '#00C896' : '#6b7280', minWidth: 22, textAlign: 'right' }}>
                  {brandSearchItems.length}
                </Typography>
              </Box>

              {categoryRailEntries.map((cat) => {
                const isSelected = selectedCategory.toLowerCase() === cat.name.toLowerCase();
                const brandColor = cat.brand === 'saheeh' ? '#f97316' : cat.brand === 'pizza_brand' ? '#60a5fa' : '#9ca3af';
                return (
                  <Box
                    key={cat.name}
                    onClick={() => setSelectedCategory(cat.name)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.2,
                      py: 1,
                      mb: 0.4,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      backgroundColor: isSelected ? 'rgba(0, 200, 150,0.12)' : 'transparent',
                      border: '1px solid',
                      borderColor: isSelected ? 'rgba(0, 200, 150,0.3)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.06)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0, flex: 1 }}>
                      {cat.image_url ? (
                        <Box component="img" src={cat.image_url} alt={cat.name} sx={{ width: 22, height: 22, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <Box sx={{ fontSize: '1rem', width: 22, textAlign: 'center', flexShrink: 0 }}>{cat.icon}</Box>
                      )}
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.82rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? '#00C896' : '#e0e0e0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cat.name}
                      </Typography>
                      {brandFilter === 'all' && cat.brand !== 'both' && (
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: brandColor, flexShrink: 0 }} />
                      )}
                    </Box>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: isSelected ? '#00C896' : '#6b7280', minWidth: 22, textAlign: 'right', flexShrink: 0 }}>
                      {cat.count}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* ── Item grid ──────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Bulk action bar */}
              {selectedCount > 0 && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.2,
                  mb: 1.5,
                  backgroundColor: 'rgba(0, 200, 150,0.1)',
                  border: '1px solid rgba(0, 200, 150,0.35)',
                  borderRadius: 2,
                  flexWrap: 'wrap',
                  flexShrink: 0,
                }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#00C896' }}>
                    {selectedCount} selected
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button size="small" onClick={selectAllVisible} sx={{ textTransform: 'none', color: '#9ca3af', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>Select all visible</Button>
                  <Button size="small" onClick={() => bulkToggleAvailable(true)} sx={{ textTransform: 'none', color: '#4CAF50', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>Make available</Button>
                  <Button size="small" onClick={() => bulkToggleAvailable(false)} sx={{ textTransform: 'none', color: '#f59e0b', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>Hide</Button>
                  <Button size="small" onClick={bulkDelete} sx={{ textTransform: 'none', color: '#ef4444', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>Delete</Button>
                  <Button size="small" onClick={clearSelection} sx={{ textTransform: 'none', color: '#9ca3af', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>Clear</Button>
                </Box>
              )}

              {/* Category heading + result count */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1.2, flexShrink: 0, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '1.15rem', fontWeight: 700, color: '#f0f2f5' }}>
                    {selectedCategory === 'all'
                      ? (brandFilter === 'all' ? '🧾 POS menu' : brandFilter === 'saheeh' ? '🍽️ Saheeh menu' : '🍕 Pizza Brand menu')
                      : selectedCategory}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280', mt: 0.15 }}>
                    {brandFilter === 'all'
                      ? 'Cashier POS view — drag cards within a category to reorder (writes shared POS order).'
                      : brandFilter === 'saheeh'
                      ? 'Customer-facing Saheeh storefront — drag to reorder this brand only.'
                      : 'Customer-facing Pizza Brand storefront — drag to reorder this brand only.'}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                  {filteredMenuItems.length} item{filteredMenuItems.length === 1 ? '' : 's'}
                  {searchQuery && ` matching "${searchQuery}"`}
                </Typography>
              </Box>

              {/* Cards grid */}
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 4 },
              }}>
                {filteredMenuItems.length === 0 ? (
                  <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    py: 8, opacity: 0.7,
                  }}>
                    <RestaurantMenu sx={{ fontSize: 56, color: '#3a3d52', mb: 1.5 }} />
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#9ca3af', fontSize: '0.95rem', fontWeight: 600 }}>
                      {searchQuery ? t('menu.noItemsMatching', { query: searchQuery }) : t('menu.noItems')}
                    </Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', color: '#6b7280', fontSize: '0.78rem', mt: 0.5 }}>
                      Try a different category or clear your filters.
                    </Typography>
                  </Box>
                ) : (
                  // Group by category, render a DndContext per group so drag
                  // is restricted within the same category (cross-category
                  // moves would silently re-categorize items — we want an
                  // explicit edit for that).
                  (() => {
                    const groups: Array<{ category: string; items: MenuItem[] }> = [];
                    for (const it of filteredMenuItems) {
                      const cat = it.category || 'Uncategorized';
                      let g = groups.find(x => x.category === cat);
                      if (!g) { g = { category: cat, items: [] }; groups.push(g); }
                      g.items.push(it);
                    }
                    return groups.map((group, gIdx) => (
                      <CategoryDndGroup
                        key={group.category}
                        showHeader={selectedCategory === 'all'}
                        firstGroup={gIdx === 0}
                        category={group.category}
                        items={group.items}
                        selectedItemIds={selectedItemIds}
                        formatPrice={formatPrice}
                        onToggleSelect={toggleItemSelection}
                        onToggleSoldOut={handleToggleSoldOut}
                        onEdit={handleEditItem}
                        onDelete={handleDeleteClick}
                        onMoveUp={(id) => handleMoveItem(id, 'up', group.items)}
                        onMoveDown={(id) => handleMoveItem(id, 'down', group.items)}
                        onDragReorder={(orderedIds) => reorderItemsByIds(orderedIds, group.items)}
                      />
                    ));
                  })()
                )}
              </Box>
            </Box>
          </Box>
        )}


        {/* Import Menu Dialog (CSV) — auto-backup + dry-run preview + undo */}
        <ImportMenuDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          existingItems={adminMenuItemsForImport}
          categories={categories.map(c => ({ name: c.name }))}
          currentUserEmail={auth.currentUser?.email || ''}
          onImportComplete={async () => {
            // Refresh menu items so the grid reflects the new state
            try {
              const refreshed = await firebaseAdminService.getMenuItems();
              setMenuItems(refreshed.map(mapAdminItemToLocal));
            } catch (e) {
              console.error('Failed to refresh after import:', e);
            }
          }}
        />

        {/* Add/Edit Menu Item Dialog — two-pane layout with live preview */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); handleSave(); } }}
          PaperProps={{ sx: { backgroundColor: '#0b0d18', backgroundImage: 'linear-gradient(180deg, #111427 0%, #0b0d18 100%)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, color: '#f0f2f5', maxHeight: '92vh' } }}
          BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(3,5,12,0.75)' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: 'rgba(0, 200, 150,0.12)', border: '1px solid rgba(0, 200, 150,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                {editingItem ? '✏️' : '✨'}
              </Box>
              <Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#f0f2f5' }}>
                  {editingItem ? t('menu.editMenuItem') : t('menu.addNewMenuItem')}
                </Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280' }}>
                  ⌘S to save · Esc to cancel
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setDialogOpen(false)} sx={{ color: '#9ca3af', '&:hover': { color: '#f0f2f5', backgroundColor: 'rgba(255,255,255,0.06)' } }}>
              <Cancel />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { md: 580 } }}>
              {/* ── Left pane: sectioned form ─────────────────────── */}
              <Box sx={{ flex: 1, p: 3, overflowY: 'auto', maxHeight: { md: '72vh' }, minWidth: 0, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 3 } }}>
                {/* § Basics ─────────────────────── */}
                <FormSection icon="📝" title="Basics" subtitle="Names and descriptions">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    <DarkTextField label={`${t('menu.itemNameEn')} (auto-fills from DE)`} value={formData.name} onChange={(v) => handleInputChange('name', v)} />
                    <DarkTextField label={`${t('menu.itemNameDe')} (auto-fills from EN)`} value={formData.name_de} onChange={(v) => handleInputChange('name_de', v)} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                    <DarkTextField label={t('menu.descriptionEn')} value={formData.description} onChange={(v) => handleInputChange('description', v)} multiline rows={2} />
                    <DarkTextField label={t('menu.descriptionDe')} value={formData.description_de} onChange={(v) => handleInputChange('description_de', v)} multiline rows={2} />
                  </Box>
                </FormSection>

                {/* § Pricing ─────────────────────── */}
                <FormSection icon="💰" title="Pricing" subtitle="Final price and tax rate">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                    <DarkTextField
                      label={t('menu.priceWithTax')}
                      value={formData.base_price}
                      onChange={(v) => handleInputChange('base_price', v)}
                      type="number"
                      required
                      inputProps={{ step: "0.01", min: "0" }}
                      startAdornment="€"
                    />
                    <Box>
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('menu.taxRate')}</Typography>
                      <Box sx={{ display: 'flex', backgroundColor: 'rgba(11,13,24,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 0.3 }}>
                        {[7, 19].map((rate) => (
                          <Box
                            key={rate}
                            onClick={() => handleInputChange('tax_rate', String(rate))}
                            sx={{
                              flex: 1,
                              textAlign: 'center',
                              py: 1.15,
                              borderRadius: 1.5,
                              cursor: 'pointer',
                              fontFamily: '"Poppins", sans-serif',
                              fontSize: '0.85rem',
                              fontWeight: 700,
                              color: String(formData.tax_rate) === String(rate) ? '#0b0d18' : '#9ca3af',
                              backgroundColor: String(formData.tax_rate) === String(rate) ? '#00C896' : 'transparent',
                              transition: 'all 0.15s',
                              userSelect: 'none',
                              '&:hover': { color: String(formData.tax_rate) === String(rate) ? '#0b0d18' : '#f0f2f5' },
                            }}
                          >
                            {rate}%
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  {(() => {
                    const price = parseFloat(String(formData.base_price || 0)) || 0;
                    const tax = parseFloat(String(formData.tax_rate || 0)) || 0;
                    const base = tax > 0 ? price / (1 + tax / 100) : price;
                    const taxAmount = price - base;
                    return (
                      <Box sx={{ mt: 1.5, p: 1.25, backgroundColor: 'rgba(0, 200, 150,0.05)', border: '1px dashed rgba(0, 200, 150,0.2)', borderRadius: 2, display: 'flex', gap: 2.5, flexWrap: 'wrap', fontFamily: '"Poppins", sans-serif', fontSize: '0.75rem' }}>
                        <Box><Typography component="span" sx={{ color: '#6b7280', fontSize: 'inherit', fontFamily: 'inherit' }}>Base price:</Typography> <Typography component="span" sx={{ color: '#f0f2f5', fontWeight: 700, fontSize: 'inherit', fontFamily: 'inherit' }}>€{base.toFixed(2)}</Typography></Box>
                        <Box><Typography component="span" sx={{ color: '#6b7280', fontSize: 'inherit', fontFamily: 'inherit' }}>Tax ({tax}%):</Typography> <Typography component="span" sx={{ color: '#f0f2f5', fontWeight: 700, fontSize: 'inherit', fontFamily: 'inherit' }}>€{taxAmount.toFixed(2)}</Typography></Box>
                        <Box sx={{ ml: 'auto' }}><Typography component="span" sx={{ color: '#6b7280', fontSize: 'inherit', fontFamily: 'inherit' }}>Customer pays:</Typography> <Typography component="span" sx={{ color: '#00C896', fontWeight: 800, fontSize: '0.85rem', fontFamily: 'inherit' }}>€{price.toFixed(2)}</Typography></Box>
                      </Box>
                    );
                  })()}
                </FormSection>

                {/* § Classification ─────────────────────── */}
                <FormSection icon="🏷️" title="Classification" subtitle="Category, storefront, and modifiers">
                  <DarkSelect
                    label={t('menu.category')}
                    value={formData.category}
                    onChange={(v) => handleInputChange('category', v)}
                    required
                    options={categories.map((c) => ({ value: c.name, label: `${c.icon || '🍽️'}  ${c.name}` }))}
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Storefront</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 0.75 }}>
                      {([
                        { key: 'auto', label: 'Auto', desc: 'from category' },
                        { key: 'saheeh', label: 'Saheeh', desc: 'saheeh-riyadh' },
                        { key: 'pizza_brand', label: 'Pizza Brand', desc: 'pizza_brand-huerth' },
                        { key: 'both', label: 'Both', desc: 'all storefronts' },
                      ] as const).map((opt) => {
                        const active = formData.brand === opt.key;
                        return (
                          <Box
                            key={opt.key}
                            onClick={() => handleInputChange('brand', opt.key)}
                            sx={{
                              p: 1.2,
                              borderRadius: 2,
                              cursor: 'pointer',
                              border: '1px solid',
                              borderColor: active ? '#00C896' : 'rgba(255,255,255,0.06)',
                              backgroundColor: active ? 'rgba(0, 200, 150,0.08)' : 'rgba(11,13,24,0.6)',
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: active ? '#00C896' : 'rgba(0, 200, 150,0.25)', backgroundColor: active ? 'rgba(0, 200, 150,0.08)' : 'rgba(0, 200, 150,0.04)' },
                            }}
                          >
                            <Box sx={{ mb: 0.4, height: 20, display: 'flex', alignItems: 'center' }}>
                              <BrandMark brand={opt.key as any} active={false} size={20} />
                            </Box>
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', fontWeight: 700, color: active ? '#00C896' : '#f0f2f5' }}>{opt.label}</Typography>
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.66rem', color: '#6b7280', mt: 0.15 }}>{opt.desc}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Modifier groups</Typography>
                    {availableModifierGroups.length === 0 ? (
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.8rem', color: '#6b7280', p: 1.5, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 2 }}>
                        No modifier groups defined yet. Go to "Modifier Groups" in sidebar to create them.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {availableModifierGroups.map((g) => {
                          const selected = (formData.modifier_group_ids as string[]).includes(g.id);
                          return (
                            <Chip
                              key={g.id}
                              label={`${g.name}${g.required ? ' ★' : ''}`}
                              onClick={() => {
                                const ids = formData.modifier_group_ids as string[];
                                const next = selected ? ids.filter(i => i !== g.id) : [...ids, g.id];
                                handleInputChange('modifier_group_ids', next);
                              }}
                              sx={{
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: selected ? '#00C896' : 'rgba(255,255,255,0.1)',
                                backgroundColor: selected ? 'rgba(0, 200, 150,0.15)' : 'rgba(11,13,24,0.6)',
                                color: selected ? '#00C896' : '#9ca3af',
                                '&:hover': { backgroundColor: selected ? 'rgba(0, 200, 150,0.2)' : 'rgba(0, 200, 150,0.08)' },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </FormSection>

                {/* § Image ─────────────────────── */}
                <FormSection icon="🖼️" title="Image" subtitle="Upload a photo or paste a URL">
                <DarkTextField label={t('menu.imageUrl')} value={formData.image_url} onChange={(v) => handleInputChange('image_url', v)} placeholder="https://example.com/image.jpg" />
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="menu-item-image-upload"
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file && formData.category) {
                      try {
                        // Import image service
                        const { firebaseImageService } = await import('../../services/firebaseImageService');
                        
                        // For new items, use temporary ID; for existing, use item ID
                        const itemId = editingItem?.id?.toString() || `temp_${Date.now()}`;
                        
                        const result = await firebaseImageService.uploadImage({
                          file,
                          folder: 'menu_items',
                          itemId,
                          categoryName: formData.category,
                        });
                        
                        if (result.success && result.url) {
                          const imageUrl = result.url;
                          console.log('✅ [MENU ITEM] Image upload successful, URL:', imageUrl);
                          handleInputChange('image_url', imageUrl);
                          console.log('✅ [MENU ITEM] Image URL set in form state');
                          console.log('✅ Image uploaded to:', result.path);
                          console.log('✅ Image URL set in form:', imageUrl);
                          
                          // Auto-save the image URL if editing an existing menu item
                          if (editingItem && editingItem.id) {
                            try {
                              console.log('💾 [MENU ITEM] Auto-saving image URL for item:', editingItem.id);
                              await firebaseAdminService.updateMenuItem(editingItem.id.toString(), {
                                image_url: imageUrl
                              });
                              console.log('✅ [MENU ITEM] Image URL auto-saved to Firestore');
                              
                              // Refresh menu items to show updated image
                              const itemsData = await firebaseAdminService.getMenuItems();
                              const fixedItems: MenuItem[] = itemsData.map(mapAdminItemToLocal);
                              setMenuItems(fixedItems);
                            } catch (error: any) {
                              console.error('❌ [MENU ITEM] Failed to auto-save image URL:', error);
                            }
                          }
                        } else {
                          alert(`Failed to upload image: ${result.error}`);
                        }
                      } catch (error: any) {
                        console.error('Image upload error:', error);
                        alert(`Failed to upload image: ${error.message}`);
                      }
                    } else if (!formData.category) {
                      alert('Please select a category first before uploading image');
                    }
                  }}
                />
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <label htmlFor="menu-item-image-upload" style={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<ImageIcon />}
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: 'rgba(0, 200, 150,0.3)',
                        color: '#00C896',
                        backgroundColor: 'rgba(0, 200, 150,0.04)',
                        borderRadius: 2,
                        py: 1.1,
                        '&:hover': {
                          borderColor: '#00C896',
                          backgroundColor: 'rgba(0, 200, 150,0.1)',
                        }
                      }}
                    >
                      {t('menu.uploadImageFile')}
                    </Button>
                  </label>
                  {formData.image_url && (
                    <Button
                      variant="outlined"
                      onClick={() => handleInputChange('image_url', '')}
                      sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, textTransform: 'none', borderColor: 'rgba(239,68,68,0.35)', color: '#ef4444', borderRadius: 2, '&:hover': { borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)' } }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                </FormSection>

                {/* § Availability ─────────────────────── */}
                <FormSection icon="⚙️" title="Availability & tags" subtitle="Visibility and dietary badges">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                    {([
                      { key: 'is_available', label: t('common.available'), desc: 'Show on storefront + POS', color: '#4CAF50' },
                      { key: 'is_popular', label: t('menu.popular'), desc: 'Mark as featured', color: '#f59e0b' },
                      { key: 'is_vegetarian', label: t('menu.vegetarian'), desc: 'Dietary badge', color: '#10b981' },
                      { key: 'is_vegan', label: t('menu.vegan'), desc: 'Dietary badge', color: '#059669' },
                    ] as const).map((tog) => {
                      const on = (formData as any)[tog.key];
                      return (
                        <Box
                          key={tog.key}
                          onClick={() => handleInputChange(tog.key, !on)}
                          sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                            p: 1.2, borderRadius: 2, cursor: 'pointer',
                            border: '1px solid',
                            borderColor: on ? tog.color : 'rgba(255,255,255,0.06)',
                            backgroundColor: on ? `${tog.color}14` : 'rgba(11,13,24,0.6)',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: on ? tog.color : 'rgba(0, 200, 150,0.25)' },
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.82rem', fontWeight: 700, color: on ? tog.color : '#f0f2f5' }}>{tog.label}</Typography>
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', color: '#6b7280' }}>{tog.desc}</Typography>
                          </Box>
                          <Switch checked={on} onClick={(e) => e.stopPropagation()} onChange={(e) => handleInputChange(tog.key, e.target.checked)} size="small" sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: tog.color },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: tog.color },
                          }} />
                        </Box>
                      );
                    })}
                  </Box>
                </FormSection>
              </Box>

              {/* ── Right pane: live preview ─────────────────────── */}
              <Box sx={{
                width: { xs: '100%', md: 340 },
                flexShrink: 0,
                backgroundColor: 'rgba(7,9,18,0.7)',
                borderLeft: { md: '1px solid rgba(255,255,255,0.05)' },
                borderTop: { xs: '1px solid rgba(255,255,255,0.05)', md: 'none' },
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                overflowY: 'auto',
                maxHeight: { md: '72vh' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Visibility sx={{ fontSize: 16, color: '#00C896' }} />
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '1px' }}>Live preview</Typography>
                </Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280', mt: -1 }}>
                  How this item will appear in the menu grid
                </Typography>
                <Box sx={{
                  backgroundColor: 'rgba(22,25,40,0.95)',
                  border: '1px solid rgba(0, 200, 150,0.25)',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                }}>
                  <Box sx={{ position: 'relative', aspectRatio: '1 / 1', backgroundColor: '#0d0f1a' }}>
                    {formData.image_url ? (
                      <Box component="img" src={formData.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e: any) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#3a3d52' }}>
                        <BrokenImage sx={{ fontSize: 54 }} />
                      </Box>
                    )}
                    {formData.is_popular && (
                      <Box sx={{ position: 'absolute', top: 8, left: 8, px: 0.8, py: 0.3, backgroundColor: 'rgba(245,158,11,0.95)', color: '#1a1d2e', fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem', fontWeight: 700, borderRadius: 1 }}>⭐ POPULAR</Box>
                    )}
                    {!formData.is_available && (
                      <Box sx={{ position: 'absolute', top: 8, right: 8, px: 0.8, py: 0.3, backgroundColor: 'rgba(239,68,68,0.95)', color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem', fontWeight: 700, borderRadius: 1 }}>HIDDEN</Box>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#f0f2f5', lineHeight: 1.25 }}>
                      {formData.name || 'Item name'}
                    </Typography>
                    {formData.name_de && formData.name_de !== formData.name && (
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280', mt: 0.1 }}>
                        {formData.name_de}
                      </Typography>
                    )}
                    {(formData.description || formData.description_de) && (
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#9ca3af', mt: 0.6, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {formData.description || formData.description_de}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '1.05rem', fontWeight: 800, color: '#00C896' }}>
                        €{(parseFloat(String(formData.base_price || 0)) || 0).toFixed(2)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        {formData.is_vegetarian && <Box sx={{ px: 0.6, py: 0.15, borderRadius: 0.8, backgroundColor: 'rgba(16,185,129,0.15)', color: '#10b981', fontFamily: '"Poppins", sans-serif', fontSize: '0.6rem', fontWeight: 700 }}>VEG</Box>}
                        {formData.is_vegan && <Box sx={{ px: 0.6, py: 0.15, borderRadius: 0.8, backgroundColor: 'rgba(5,150,105,0.15)', color: '#059669', fontFamily: '"Poppins", sans-serif', fontSize: '0.6rem', fontWeight: 700 }}>VEGAN</Box>}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 1, p: 1.25, backgroundColor: 'rgba(0, 200, 150,0.04)', border: '1px solid rgba(0, 200, 150,0.1)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', fontWeight: 700, color: '#00C896', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.5 }}>Classification</Typography>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', color: '#f0f2f5' }}>
                    {formData.category || <Typography component="span" sx={{ color: '#6b7280', fontFamily: 'inherit', fontSize: 'inherit' }}>— no category —</Typography>}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', color: '#9ca3af', mt: 0.3 }}>
                    {formData.brand === 'auto' ? 'Storefront: auto-derived' : formData.brand === 'saheeh' ? 'Storefront: 🍽️ Saheeh only' : formData.brand === 'pizza_brand' ? 'Storefront: 🍕 Pizza Brand only' : 'Storefront: both'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 3, py: 2, borderTop: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(7,9,18,0.4)' }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600, textTransform: 'none', color: '#9ca3af', borderRadius: 2, px: 2, '&:hover': { color: '#f0f2f5', backgroundColor: 'rgba(255,255,255,0.05)' } }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                backgroundColor: '#00C896',
                color: '#0b0d18',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                boxShadow: '0 4px 14px rgba(0, 200, 150,0.3)',
                '&:hover': { backgroundColor: '#7bc912', boxShadow: '0 6px 22px rgba(0, 200, 150,0.4)' }
              }}
            >
              {editingItem ? t('common.update') : t('common.create')}
            </Button>
          </Box>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
            {t('common.confirmDelete')}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('menu.deleteItemConfirm', { name: itemToDelete?.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category Edit/Add Dialog */}
        <Dialog 
          open={categoryEditDialogOpen} 
          onClose={() => {
            setCategoryEditDialogOpen(false);
            setEditingCategory(null);
          }} 
          maxWidth="md" fullWidth
        >
          <DialogTitle sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
            {editingCategory ? t('menu.editCategory') : t('menu.addNewCategory')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label={t('menu.categoryName')}
                value={categoryFormData.name}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <TextField
                label={t('menu.iconEmoji')}
                value={categoryFormData.icon}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, icon: e.target.value }))}
                fullWidth
                placeholder="🍽️"
                helperText={t('menu.emojiHelper')}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label={t('menu.categoryImageUrl')}
                  value={categoryFormData.image_url}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  fullWidth
                  placeholder="https://example.com/image.jpg or upload file"
                  sx={{ fontFamily: '"Poppins", sans-serif' }}
                  helperText={t('menu.imageUrlHelper')}
                />
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="category-image-upload"
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file && categoryFormData.name) {
                      try {
                        const { firebaseImageService } = await import('../../services/firebaseImageService');
                        
                        const result = await firebaseImageService.uploadImage({
                          file,
                          folder: 'categories',
                          categoryName: categoryFormData.name,
                        });
                        
                        if (result.success && result.url) {
                          const imageUrl = result.url;
                          setCategoryFormData(prev => ({ ...prev, image_url: imageUrl }));
                          console.log('✅ Category image uploaded to:', result.path);
                          console.log('✅ Category image URL set in form:', imageUrl);
                          
                          // Always try to save the image URL immediately
                          // First try with editingCategory, then try to find by name
                          let categoryIdToSave: string | null = null;
                          
                          if (editingCategory && editingCategory.id) {
                            categoryIdToSave = editingCategory.id;
                            console.log('💾 [CATEGORY] Using editingCategory ID:', categoryIdToSave);
                          } else if (categoryFormData.name) {
                            // Try to find category by name
                            const categoryToUpdate = categories.find(cat => 
                              cat.name.toLowerCase() === categoryFormData.name.toLowerCase()
                            );
                            if (categoryToUpdate && categoryToUpdate.id) {
                              categoryIdToSave = categoryToUpdate.id;
                              console.log('💾 [CATEGORY] Found category by name, ID:', categoryIdToSave);
                            }
                          }
                          
                          if (categoryIdToSave) {
                            try {
                              console.log('💾 [CATEGORY] Auto-saving image URL for category:', categoryIdToSave);
                              await firebaseAdminService.updateCategory(categoryIdToSave, {
                                image_url: imageUrl
                              });
                              console.log('✅ [CATEGORY] Image URL auto-saved to Firestore');
                              
                              // Refresh categories to show updated image
                              const categoriesData = await firebaseAdminService.getCategories();
                              setCategories(categoriesData);
                              
                              // Clear any pending upload
                              setPendingImageUpload(null);
                            } catch (error: any) {
                              console.error('❌ [CATEGORY] Failed to auto-save image URL:', error);
                              // Store as pending if category not found yet
                              if (categoryFormData.name) {
                                setPendingImageUpload({ url: imageUrl, categoryId: categoryFormData.name });
                                console.log('⚠️ [CATEGORY] Stored as pending upload, will save when category is created');
                              }
                            }
                          } else {
                            console.warn('⚠️ [CATEGORY] Category ID not found, storing as pending');
                            if (categoryFormData.name) {
                              setPendingImageUpload({ url: imageUrl, categoryId: categoryFormData.name });
                            }
                          }
                        } else {
                          alert(`Failed to upload image: ${result.error}`);
                        }
                      } catch (error: any) {
                        console.error('Image upload error:', error);
                        alert(`Failed to upload image: ${error.message}`);
                      }
                    } else if (!categoryFormData.name) {
                      alert('Please enter category name first before uploading image');
                    }
                  }}
                />
                <label htmlFor="category-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      borderColor: '#000000',
                      color: '#e0e0e0',
                      '&:hover': {
                        borderColor: '#333333',
                        backgroundColor: '#1e2139'
                      }
                    }}
                  >
                    {t('menu.uploadCategoryImage')}
                  </Button>
                </label>
              </Box>
              {categoryFormData.image_url && (
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{ fontFamily: '"Poppins", sans-serif', display: 'block', mb: 1 }}>
                    {t('menu.imagePreview')}:
                  </Typography>
                  <Box
                    component="img"
                    src={categoryFormData.image_url}
                    alt="Category Preview"
                    sx={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      display: 'block'
                    }}
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
              <TextField
                label={t('menu.displayOrder')}
                type="number"
                value={categoryFormData.display_order}
                onChange={(e) => setCategoryFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                fullWidth
                inputProps={{ min: 0 }}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
                helperText={t('menu.lowerNumbersFirst')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryFormData.is_active}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={t('common.active')}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCategoryEditDialogOpen(false);
                setEditingCategory(null);
              }}
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                try {
                  setError(null); // Clear any previous errors
                  
                  // Validate required fields
                  if (!categoryFormData.name || categoryFormData.name.trim() === '') {
                    setError('Category name is required');
                    return;
                  }
                  
                  console.log('💾 [CATEGORY] Saving category:', {
                    editing: !!editingCategory,
                    categoryId: editingCategory?.id,
                    data: categoryFormData
                  });
                  
                  // Check if there's a pending image upload that needs to be saved
                  let finalCategoryData = { ...categoryFormData };
                  if (pendingImageUpload && pendingImageUpload.categoryId === categoryFormData.name) {
                    console.log('💾 [CATEGORY] Found pending image upload, including in save:', pendingImageUpload.url);
                    finalCategoryData.image_url = pendingImageUpload.url;
                    setPendingImageUpload(null);
                  }
                  
                  let savedCategoryId: string;
                  
                  if (editingCategory) {
                    await firebaseAdminService.updateCategory(editingCategory.id, finalCategoryData);
                    savedCategoryId = editingCategory.id;
                    console.log('✅ [CATEGORY] Category updated successfully');
                    console.log('✅ [CATEGORY] Image URL in update:', finalCategoryData.image_url || 'EMPTY');
                  } else {
                    const newCategory = await firebaseAdminService.createCategory(finalCategoryData);
                    savedCategoryId = newCategory.id;
                    console.log('✅ [CATEGORY] Category created successfully');
                    
                    // If there was a pending image upload, save it now
                    if (pendingImageUpload && pendingImageUpload.url) {
                      try {
                        await firebaseAdminService.updateCategory(savedCategoryId, {
                          image_url: pendingImageUpload.url
                        });
                        console.log('✅ [CATEGORY] Pending image URL saved after category creation');
                        setPendingImageUpload(null);
                      } catch (error: any) {
                        console.error('❌ [CATEGORY] Failed to save pending image URL:', error);
                      }
                    }
                  }
                  
                  // Close dialog and reset state
                  setCategoryEditDialogOpen(false);
                  setEditingCategory(null);
                  
                  // Refresh categories
                  const categoriesData = await firebaseAdminService.getCategories();
                  setCategories(categoriesData);
                  
                  console.log('✅ [CATEGORY] Categories refreshed:', categoriesData.length);
                } catch (error: any) {
                  console.error('❌ [CATEGORY] Save error:', error);
                  const errorMessage = error?.message || error?.code || 'Unknown error occurred';
                  setError(`Failed to ${editingCategory ? 'update' : 'create'} category: ${errorMessage}`);
                  // Don't close dialog on error so user can fix and retry
                }
              }}
              variant="contained"
              sx={{ 
                backgroundColor: '#00C896',
                color: '#1a1d2e',
                fontFamily: '"Poppins", sans-serif',
                '&:hover': { backgroundColor: '#7bc912' }
              }}
            >
              {editingCategory ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Category List Dialog — tile-board layout with brand scope switcher */}
        <Dialog
          open={categoryListDialogOpen}
          onClose={() => setCategoryListDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#0b0d18', backgroundImage: 'linear-gradient(180deg, #111427 0%, #0b0d18 100%)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, color: '#f0f2f5', maxHeight: '90vh' } }}
          BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(3,5,12,0.75)' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: 'rgba(0, 200, 150,0.12)', border: '1px solid rgba(0, 200, 150,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🗂️</Box>
              <Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#f0f2f5' }}>{t('menu.manageCategories')}</Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280' }}>Drag tiles to reorder · click to edit · Esc to close</Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setCategoryListDialogOpen(false)} sx={{ color: '#9ca3af', '&:hover': { color: '#f0f2f5', backgroundColor: 'rgba(255,255,255,0.06)' } }}><Cancel /></IconButton>
          </Box>

          <DialogContent sx={{ p: 3, overflow: 'auto' }}>
            {/* Brand scope switcher */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Arranging for:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, backgroundColor: 'rgba(11,13,24,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 0.35 }}>
                {([
                  { key: 'all', label: 'POS' },
                  { key: 'saheeh', label: 'Saheeh' },
                  { key: 'pizza_brand', label: 'Pizza Brand' },
                ] as const).map(opt => {
                  const active = brandFilter === opt.key;
                  return (
                    <Box
                      key={opt.key}
                      onClick={() => setBrandFilter(opt.key as any)}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.6,
                        px: 1.4,
                        py: 0.5,
                        borderRadius: 1.5,
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        fontFamily: '"Poppins", sans-serif',
                        color: active ? '#0b0d18' : '#9ca3af',
                        backgroundColor: active ? '#00C896' : 'transparent',
                        transition: 'all 0.15s',
                        userSelect: 'none',
                      }}
                    >
                      <BrandMark brand={opt.key} active={active} size={16} />
                      {opt.label}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryFormData({ name: '', icon: '🍽️', image_url: '', is_active: true, display_order: categories.length });
                  setCategoryListDialogOpen(false);
                  setTimeout(() => { setCategoryEditDialogOpen(true); }, 100);
                }}
                sx={{ backgroundColor: '#00C896', color: '#0b0d18', fontFamily: '"Poppins", sans-serif', fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 2, boxShadow: '0 4px 14px rgba(0, 200, 150,0.3)', '&:hover': { backgroundColor: '#7bc912' } }}
              >
                {t('menu.addNewCategory')}
              </Button>
            </Box>

            {/* Tile grid with drag-drop */}
            <CategoryTileBoard
              categories={visibleCategoriesForBrand(categories, brandFilter).sort((a: any, b: any) => {
                const diff = pickCategoryOrder(a, brandFilter) - pickCategoryOrder(b, brandFilter);
                return diff !== 0 ? diff : (a.name || '').localeCompare(b.name || '');
              })}
              menuItems={menuItems}
              onDragReorder={reorderCategoriesByIds}
              onEdit={(category) => {
                setEditingCategory(category);
                setCategoryFormData({ name: category.name, icon: category.icon || '🍽️', image_url: category.image_url || '', is_active: category.is_active, display_order: category.display_order || 0 });
                setCategoryListDialogOpen(false);
                setTimeout(() => { setCategoryEditDialogOpen(true); }, 100);
              }}
              onDelete={(category) => { setCategoryToDelete(category); setCategoryDeleteDialogOpen(true); }}
            />

            {visibleCategoriesForBrand(categories, brandFilter).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8, color: '#6b7280' }}>
                <Category sx={{ fontSize: 56, color: '#3a3d52', mb: 1 }} />
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.95rem', fontWeight: 600, color: '#9ca3af' }}>No categories for this brand yet.</Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', mt: 0.5 }}>Click "Add New Category" above to create one.</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Category Delete Confirmation Dialog */}
        <Dialog open={categoryDeleteDialogOpen} onClose={() => setCategoryDeleteDialogOpen(false)}>
          <DialogTitle sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
            {t('common.confirmDelete')}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('menu.deleteCategoryConfirm', { name: categoryToDelete?.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDeleteDialogOpen(false)} sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                if (categoryToDelete) {
                  try {
                    await firebaseAdminService.deleteCategory(categoryToDelete.id);
                    setCategoryDeleteDialogOpen(false);
                    setCategoryToDelete(null);
                    // Refresh categories
                    const categoriesData = await firebaseAdminService.getCategories();
                    setCategories(categoriesData);
                  } catch (error) {
                    console.error('Category delete error:', error);
                    setError('Failed to delete category. Please try again.');
                  }
                }
              }}
              variant="contained"
              color="error"
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add-Ons List Dialog — grouped by price with glassy chips */}
        <Dialog
          open={addOnsListDialogOpen}
          onClose={() => setAddOnsListDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { backgroundColor: '#0b0d18', backgroundImage: 'linear-gradient(180deg, #111427 0%, #0b0d18 100%)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, color: '#f0f2f5', maxHeight: '90vh' } }}
          BackdropProps={{ sx: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(3,5,12,0.75)' } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: 'rgba(192,132,252,0.12)', border: '1px solid rgba(192,132,252,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🧂</Box>
              <Box>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#f0f2f5' }}>{t('menu.manageAddons')}</Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280' }}>Grouped by price · {addOns.length} total · {addOns.filter(a => a.is_active).length} active</Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setAddOnsListDialogOpen(false)} sx={{ color: '#9ca3af', '&:hover': { color: '#f0f2f5', backgroundColor: 'rgba(255,255,255,0.06)' } }}><Cancel /></IconButton>
          </Box>

          <DialogContent sx={{ p: 3, overflow: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditingAddOn(null);
                setAddOnFormData({ name: '', name_de: '', price: '', description: '', is_active: true, sort_order: addOns.length });
                setAddOnsListDialogOpen(false);
                setTimeout(() => { setAddOnEditDialogOpen(true); }, 100);
              }}
              sx={{ backgroundColor: '#00C896', color: '#0b0d18', fontFamily: '"Poppins", sans-serif', fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 2.5, mb: 2, boxShadow: '0 4px 14px rgba(0, 200, 150,0.3)', '&:hover': { backgroundColor: '#7bc912' } }}
            >
              {t('menu.addNewAddon')}
            </Button>

            {addOns.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: '#6b7280' }}>
                <Typography sx={{ fontSize: '3rem', mb: 1 }}>🧂</Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.95rem', fontWeight: 600, color: '#9ca3af' }}>{t('menu.noAddons')}</Typography>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.78rem', mt: 0.5 }}>Click "Add New Add-On" above to create one.</Typography>
              </Box>
            ) : (
              (() => {
                // Group by price bucket
                const buckets = new Map<number, typeof addOns>();
                const sorted = [...addOns].sort((a, b) => a.price - b.price || a.name.localeCompare(b.name));
                for (const a of sorted) {
                  const bucket = Math.round(a.price * 100) / 100;
                  const arr = buckets.get(bucket) || [];
                  arr.push(a);
                  buckets.set(bucket, arr);
                }
                const priceKeys = Array.from(buckets.keys()).sort((a, b) => a - b);
                return priceKeys.map((price) => {
                  const group = buckets.get(price)!;
                  return (
                    <Box key={price} sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{ px: 1.25, py: 0.6, borderRadius: 2, backgroundColor: 'rgba(0, 200, 150,0.1)', border: '1px solid rgba(0, 200, 150,0.25)', fontFamily: '"Poppins", sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#00C896' }}>
                          €{price.toFixed(2)}
                        </Box>
                        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>
                          {group.length} item{group.length === 1 ? '' : 's'}
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.05)' }} />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1 }}>
                        {group.map((addon) => (
                          <Box
                            key={addon.id}
                            sx={{
                              position: 'relative',
                              p: 1.25,
                              pl: 1.5,
                              borderRadius: 2,
                              backgroundColor: addon.is_active ? 'rgba(22,25,40,0.7)' : 'rgba(22,25,40,0.35)',
                              border: '1px solid',
                              borderColor: addon.is_active ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
                              opacity: addon.is_active ? 1 : 0.55,
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: 'rgba(0, 200, 150,0.3)', transform: 'translateY(-1px)' },
                              '&:hover .row-actions': { opacity: 1 },
                            }}
                          >
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#f0f2f5', lineHeight: 1.2 }}>
                              {addon.name}
                            </Typography>
                            {addon.name_de && addon.name_de !== addon.name && (
                              <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.68rem', color: '#6b7280', mt: 0.2 }}>
                                {addon.name_de}
                              </Typography>
                            )}
                            <Box className="row-actions" sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.3, opacity: 0, transition: 'opacity 0.15s' }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingAddOn(addon);
                                  setAddOnFormData({ name: addon.name, name_de: addon.name_de || addon.name, price: addon.price.toString(), description: addon.description || '', is_active: addon.is_active, sort_order: addon.sort_order || 0 });
                                  setAddOnsListDialogOpen(false);
                                  setTimeout(() => { setAddOnEditDialogOpen(true); }, 100);
                                }}
                                sx={{ width: 22, height: 22, backgroundColor: 'rgba(11,13,24,0.85)', color: '#9ca3af', '&:hover': { color: '#00C896' } }}
                              >
                                <Edit sx={{ fontSize: 12 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => { setAddOnToDelete(addon); setAddOnDeleteDialogOpen(true); }}
                                sx={{ width: 22, height: 22, backgroundColor: 'rgba(11,13,24,0.85)', color: '#9ca3af', '&:hover': { color: '#ef4444' } }}
                              >
                                <Delete sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                            {!addon.is_active && (
                              <Box sx={{ mt: 0.5, display: 'inline-block', px: 0.6, py: 0.1, borderRadius: 0.7, backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', fontFamily: '"Poppins", sans-serif', fontSize: '0.6rem', fontWeight: 700 }}>INACTIVE</Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                });
              })()
            )}
          </DialogContent>
        </Dialog>

        {/* Add-On Edit/Add Dialog */}
        <Dialog 
          open={addOnEditDialogOpen} 
          onClose={() => {
            setAddOnEditDialogOpen(false);
            setEditingAddOn(null);
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
            {editingAddOn ? t('menu.editAddon') : t('menu.addNewAddon')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label={t('menu.addonNameEn')}
                value={addOnFormData.name}
                onChange={(e) => setAddOnFormData(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <TextField
                label={t('menu.addonNameDe')}
                value={addOnFormData.name_de}
                onChange={(e) => setAddOnFormData(prev => ({ ...prev, name_de: e.target.value }))}
                fullWidth
                required
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <TextField
                label={t('menu.priceEuro')}
                type="number"
                value={addOnFormData.price}
                onChange={(e) => setAddOnFormData(prev => ({ ...prev, price: e.target.value }))}
                fullWidth
                required
                inputProps={{ step: "0.01", min: "0" }}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <TextField
                label={t('common.description')}
                value={addOnFormData.description}
                onChange={(e) => setAddOnFormData(prev => ({ ...prev, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
              <TextField
                label={t('menu.sortOrder')}
                type="number"
                value={addOnFormData.sort_order}
                onChange={(e) => setAddOnFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                fullWidth
                inputProps={{ min: 0 }}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
                helperText={t('menu.lowerNumbersFirst')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={addOnFormData.is_active}
                    onChange={(e) => setAddOnFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={t('common.active')}
                sx={{ fontFamily: '"Poppins", sans-serif' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setAddOnEditDialogOpen(false);
                setEditingAddOn(null);
              }}
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                try {
                  setError(null);
                  
                  if (!addOnFormData.name || addOnFormData.name.trim() === '') {
                    setError('Add-on name is required');
                    return;
                  }
                  
                  if (!addOnFormData.price || parseFloat(addOnFormData.price) <= 0) {
                    setError('Please enter a valid price greater than 0');
                    return;
                  }
                  
                  if (editingAddOn) {
                    await firebaseAdminService.updateAddOn(editingAddOn.id, {
                      name: addOnFormData.name,
                      name_de: addOnFormData.name_de || addOnFormData.name,
                      price: parseFloat(addOnFormData.price),
                      description: addOnFormData.description,
                      is_active: addOnFormData.is_active,
                      sort_order: addOnFormData.sort_order,
                    });
                  } else {
                    await firebaseAdminService.createAddOn({
                      name: addOnFormData.name,
                      name_de: addOnFormData.name_de || addOnFormData.name,
                      price: parseFloat(addOnFormData.price),
                      description: addOnFormData.description,
                      is_active: addOnFormData.is_active,
                      sort_order: addOnFormData.sort_order,
                    });
                  }
                  
                  setAddOnEditDialogOpen(false);
                  setEditingAddOn(null);
                  
                  // Refresh add-ons
                  const addOnsData = await firebaseAdminService.getAddOns();
                  setAddOns(addOnsData);
                  
                  // Reopen list dialog
                  setTimeout(() => {
                    setAddOnsListDialogOpen(true);
                  }, 100);
                } catch (error: any) {
                  console.error('Add-on save error:', error);
                  setError(`Failed to ${editingAddOn ? 'update' : 'create'} add-on: ${error.message || 'Unknown error'}`);
                }
              }}
              variant="contained"
              sx={{ 
                backgroundColor: '#00C896',
                color: '#1a1d2e',
                fontFamily: '"Poppins", sans-serif',
                '&:hover': { backgroundColor: '#7bc912' }
              }}
            >
              {editingAddOn ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add-On Delete Confirmation Dialog */}
        <Dialog open={addOnDeleteDialogOpen} onClose={() => setAddOnDeleteDialogOpen(false)}>
          <DialogTitle sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 600 }}>
            {t('common.confirmDelete')}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('menu.deleteAddonConfirm', { name: addOnToDelete?.name })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOnDeleteDialogOpen(false)} sx={{ fontFamily: '"Poppins", sans-serif' }}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                if (addOnToDelete) {
                  try {
                    await firebaseAdminService.deleteAddOn(addOnToDelete.id);
                    setAddOnDeleteDialogOpen(false);
                    setAddOnToDelete(null);
                    // Refresh add-ons
                    const addOnsData = await firebaseAdminService.getAddOns();
                    setAddOns(addOnsData);
                    // Reopen list dialog
                    setTimeout(() => {
                      setAddOnsListDialogOpen(true);
                    }, 100);
                  } catch (error) {
                    console.error('Add-on delete error:', error);
                    setError('Failed to delete add-on. Please try again.');
                  }
                }
              }}
              variant="contained"
              color="error"
              sx={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default MenuPage;

// ──────────────────────────────────────────────────────────────────────
// Drag-and-drop sortable item cards (per-category group)
// ──────────────────────────────────────────────────────────────────────
interface CategoryDndGroupProps {
  category: string;
  items: MenuItem[];
  showHeader: boolean;
  firstGroup: boolean;
  selectedItemIds: Set<string>;
  formatPrice: (p: number | string | undefined) => string;
  onToggleSelect: (id: string | number) => void;
  onToggleSoldOut: (item: MenuItem) => void;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  onMoveUp: (id: string | number) => void;
  onMoveDown: (id: string | number) => void;
  onDragReorder: (orderedIds: Array<string | number>) => void;
}

const CategoryDndGroup: React.FC<CategoryDndGroupProps> = ({
  category, items, showHeader, firstGroup,
  selectedItemIds, formatPrice,
  onToggleSelect, onToggleSoldOut, onEdit, onDelete,
  onMoveUp, onMoveDown, onDragReorder,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = items.map(i => String(i.id));
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const newOrder = arrayMove(ids, oldIdx, newIdx);
    onDragReorder(newOrder);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {showHeader && (
        <Box sx={{ mt: firstGroup ? 0 : 1.5, mb: 0.8 }}>
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {category}
          </Typography>
        </Box>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(auto-fill, minmax(150px, 1fr))',
              sm: 'repeat(auto-fill, minmax(180px, 1fr))',
              md: 'repeat(auto-fill, minmax(210px, 1fr))',
            },
            gap: 1.5,
          }}>
            {items.map((item, idx) => (
              <SortableMenuItemCard
                key={item.id}
                item={item}
                isFirst={idx === 0}
                isLast={idx === items.length - 1}
                isSelected={selectedItemIds.has(String(item.id))}
                formatPrice={formatPrice}
                onToggleSelect={onToggleSelect}
                onToggleSoldOut={onToggleSoldOut}
                onEdit={onEdit}
                onDelete={onDelete}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

interface SortableMenuItemCardProps {
  item: MenuItem;
  isFirst: boolean;
  isLast: boolean;
  isSelected: boolean;
  formatPrice: (p: number | string | undefined) => string;
  onToggleSelect: (id: string | number) => void;
  onToggleSoldOut: (item: MenuItem) => void;
  onEdit: (item: MenuItem) => void;
  onDelete: (item: MenuItem) => void;
  onMoveUp: (id: string | number) => void;
  onMoveDown: (id: string | number) => void;
}

const SortableMenuItemCard: React.FC<SortableMenuItemCardProps> = ({
  item, isFirst, isLast, isSelected, formatPrice,
  onToggleSelect, onToggleSoldOut, onEdit, onDelete, onMoveUp, onMoveDown,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(item.id) });
  const isHidden = !item.available;
  const isSoldOut = item.sold_out;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : (isHidden ? 0.55 : 1),
    zIndex: isDragging ? 3 : undefined,
    boxShadow: isDragging ? '0 16px 40px rgba(0,0,0,0.55)' : undefined,
    position: 'relative',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: 'rgba(22,25,40,0.7)',
        border: '1px solid',
        borderColor: isSelected ? '#00C896' : isDragging ? 'rgba(0, 200, 150,0.6)' : 'rgba(255,255,255,0.06)',
        borderRadius: 2.5,
        overflow: 'hidden',
        transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
        '&:hover': {
          borderColor: 'rgba(0, 200, 150,0.35)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        },
        '&:hover .hover-actions': { opacity: 1 },
      }}
    >
      {/* Image */}
      <Box sx={{ position: 'relative', aspectRatio: '1 / 1', backgroundColor: '#0d0f1a', overflow: 'hidden' }}>
        {item.image_url ? (
          <Box
            component="img"
            src={item.image_url}
            alt={item.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={(e: any) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3a3d52' }}>
            <BrokenImage sx={{ fontSize: 42 }} />
          </Box>
        )}
        {/* Drag handle */}
        <Box
          {...attributes}
          {...listeners}
          className="hover-actions"
          sx={{
            position: 'absolute', top: 8, right: 8,
            width: 28, height: 28,
            borderRadius: 1,
            backgroundColor: 'rgba(11,13,24,0.85)',
            color: '#e0e0e0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab',
            backdropFilter: 'blur(4px)',
            opacity: 0,
            transition: 'opacity 0.15s, background-color 0.15s',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
            '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.25)', color: '#00C896' },
          }}
          title="Drag to reorder"
        >
          <DragIndicator sx={{ fontSize: 16 }} />
        </Box>
        {/* Select checkbox */}
        <Box
          onClick={() => onToggleSelect(item.id)}
          className="hover-actions"
          sx={{
            position: 'absolute', top: 8, left: 8,
            width: 22, height: 22,
            borderRadius: 1,
            border: '2px solid',
            borderColor: isSelected ? '#00C896' : 'rgba(255,255,255,0.4)',
            backgroundColor: isSelected ? '#00C896' : 'rgba(11,13,24,0.7)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
            opacity: isSelected ? 1 : 0,
            backdropFilter: 'blur(4px)',
          }}
        >
          {isSelected && <CheckCircle sx={{ fontSize: 14, color: '#0d0f1a' }} />}
        </Box>
        {/* Status badges */}
        {isSoldOut && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, px: 0.8, py: 0.3, backgroundColor: 'rgba(249,115,22,0.95)', color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem', fontWeight: 700, borderRadius: 1, letterSpacing: '0.5px' }}>SOLD OUT</Box>
        )}
        {isHidden && !isSoldOut && (
          <Box sx={{ position: 'absolute', bottom: 8, left: 8, px: 0.8, py: 0.3, backgroundColor: 'rgba(239,68,68,0.95)', color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: '0.65rem', fontWeight: 700, borderRadius: 1, letterSpacing: '0.5px' }}>HIDDEN</Box>
        )}
        {/* Hover arrow buttons (fallback for single-step nudges) */}
        <Box
          className="hover-actions"
          sx={{
            position: 'absolute', bottom: 8, right: 8,
            display: 'flex', gap: 0.5,
            opacity: 0, transition: 'opacity 0.15s',
          }}
        >
          <IconButton
            size="small"
            onClick={() => onMoveUp(item.id)}
            disabled={isFirst}
            sx={{ width: 26, height: 26, backgroundColor: 'rgba(11,13,24,0.85)', color: '#e0e0e0', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.25)' }, '&.Mui-disabled': { color: '#3a3d52' } }}
            title="Move up"
          >
            <ArrowUpward sx={{ fontSize: 13 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onMoveDown(item.id)}
            disabled={isLast}
            sx={{ width: 26, height: 26, backgroundColor: 'rgba(11,13,24,0.85)', color: '#e0e0e0', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.25)' }, '&.Mui-disabled': { color: '#3a3d52' } }}
            title="Move down"
          >
            <ArrowDownward sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      </Box>
      {/* Body */}
      <Box sx={{ p: 1.3 }}>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#f0f2f5', lineHeight: 1.25, mb: 0.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.05em' }}>
          {item.name}
        </Typography>
        {item.name_de && item.name_de !== item.name && (
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.2, mb: 0.6, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {item.name_de}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.95rem', fontWeight: 800, color: '#00C896' }}>
            {formatPrice(item.price)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.2 }}>
            <IconButton size="small" onClick={() => onToggleSoldOut(item)} sx={{ p: 0.5, color: item.sold_out ? '#f97316' : '#6b7280', '&:hover': { color: item.sold_out ? '#fb923c' : '#00C896' } }} title={item.sold_out ? 'Mark in stock' : 'Mark sold out'}>
              {item.sold_out ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
            </IconButton>
            <IconButton size="small" onClick={() => onEdit(item)} sx={{ p: 0.5, color: '#9ca3af', '&:hover': { color: '#00C896' } }} title="Edit">
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(item)} sx={{ p: 0.5, color: '#6b7280', '&:hover': { color: '#ef4444' } }} title="Delete">
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Category tile board (used by the redesigned Manage Categories dialog)
// ──────────────────────────────────────────────────────────────────────
interface CategoryTileBoardProps {
  categories: AdminCategory[];
  menuItems: MenuItem[];
  onDragReorder: (orderedIds: string[]) => void;
  onEdit: (cat: AdminCategory) => void;
  onDelete: (cat: AdminCategory) => void;
}
const CategoryTileBoard: React.FC<CategoryTileBoardProps> = ({ categories, menuItems, onDragReorder, onEdit, onDelete }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = categories.map(c => c.id);
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    onDragReorder(arrayMove(ids, oldIdx, newIdx));
  };
  const itemCount = (catName: string) => menuItems.filter(m => (m.category || '').toLowerCase() === (catName || '').toLowerCase()).length;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(150px, 1fr))', sm: 'repeat(auto-fill, minmax(175px, 1fr))' }, gap: 1.5 }}>
          {categories.map(cat => (
            <CategoryTile key={cat.id} category={cat} itemCount={itemCount(cat.name)} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

interface CategoryTileProps {
  category: AdminCategory;
  itemCount: number;
  onEdit: (cat: AdminCategory) => void;
  onDelete: (cat: AdminCategory) => void;
}
const CategoryTile: React.FC<CategoryTileProps> = ({ category, itemCount, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    zIndex: isDragging ? 3 : undefined,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        backgroundColor: 'rgba(22,25,40,0.7)',
        border: '1px solid',
        borderColor: isDragging ? 'rgba(0, 200, 150,0.6)' : 'rgba(255,255,255,0.06)',
        borderRadius: 2.5,
        overflow: 'hidden',
        transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
        boxShadow: isDragging ? '0 16px 40px rgba(0,0,0,0.55)' : undefined,
        '&:hover': { borderColor: 'rgba(0, 200, 150,0.35)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' },
        '&:hover .tile-actions': { opacity: 1 },
      }}
    >
      {/* Image / icon area */}
      <Box sx={{ position: 'relative', aspectRatio: '1 / 1', backgroundColor: '#0d0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.8rem' }}>
        {category.image_url ? (
          <Box component="img" src={category.image_url} alt={category.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e: any) => { e.target.style.display = 'none'; }} />
        ) : (
          <Box sx={{ opacity: 0.85 }}>{category.icon || '🍽️'}</Box>
        )}
        {/* Drag handle */}
        <Box
          {...attributes}
          {...listeners}
          className="tile-actions"
          sx={{
            position: 'absolute', top: 6, left: 6,
            width: 26, height: 26, borderRadius: 1,
            backgroundColor: 'rgba(11,13,24,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab', backdropFilter: 'blur(4px)',
            opacity: 0, transition: 'opacity 0.15s',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
            '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.25)' },
          }}
          title="Drag to reorder"
        >
          <DragIndicator sx={{ fontSize: 15, color: '#e0e0e0' }} />
        </Box>
        {/* Item count pill */}
        <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.8, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(11,13,24,0.85)', backdropFilter: 'blur(4px)', fontFamily: '"Poppins", sans-serif', fontSize: '0.68rem', fontWeight: 700, color: '#00C896' }}>
          {itemCount}
        </Box>
        {!category.is_active && (
          <Box sx={{ position: 'absolute', bottom: 6, left: 6, px: 0.8, py: 0.25, borderRadius: 1, backgroundColor: 'rgba(239,68,68,0.95)', color: '#fff', fontFamily: '"Poppins", sans-serif', fontSize: '0.6rem', fontWeight: 700 }}>HIDDEN</Box>
        )}
        {/* Action buttons */}
        <Box
          className="tile-actions"
          sx={{
            position: 'absolute', bottom: 6, right: 6, display: 'flex', gap: 0.4,
            opacity: 0, transition: 'opacity 0.15s',
          }}
        >
          <IconButton size="small" onClick={() => onEdit(category)} sx={{ width: 26, height: 26, backgroundColor: 'rgba(11,13,24,0.85)', color: '#e0e0e0', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.25)', color: '#00C896' } }}>
            <Edit sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(category)} sx={{ width: 26, height: 26, backgroundColor: 'rgba(11,13,24,0.85)', color: '#e0e0e0', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(239,68,68,0.25)', color: '#ef4444' } }}>
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
      {/* Name */}
      <Box sx={{ px: 1.2, py: 1 }}>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#f0f2f5', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {category.name}
        </Typography>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.66rem', color: '#6b7280', mt: 0.15 }}>
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Drag-and-drop sortable rows (categories)
// ──────────────────────────────────────────────────────────────────────
interface SortableCategoryRowsProps {
  categories: AdminCategory[];
  onDragReorder: (orderedIds: string[]) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onEdit: (cat: AdminCategory) => void;
  onDelete: (cat: AdminCategory) => void;
  noImageLabel: string;
}
const SortableCategoryRows: React.FC<SortableCategoryRowsProps> = ({
  categories,
  onDragReorder,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  noImageLabel,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const ids = categories.map(c => c.id);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const newIds = arrayMove(ids, oldIdx, newIdx);
    onDragReorder(newIds);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {categories.map((category, idx) => (
          <SortableCategoryRow
            key={category.id}
            category={category}
            isFirst={idx === 0}
            isLast={idx === categories.length - 1}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onEdit={onEdit}
            onDelete={onDelete}
            noImageLabel={noImageLabel}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

interface SortableCategoryRowProps {
  category: AdminCategory;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onEdit: (cat: AdminCategory) => void;
  onDelete: (cat: AdminCategory) => void;
  noImageLabel: string;
}
const SortableCategoryRow: React.FC<SortableCategoryRowProps> = ({
  category,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  noImageLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? 'rgba(0, 200, 150,0.08)' : undefined,
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? '0 6px 22px rgba(0,0,0,0.35)' : undefined,
    zIndex: isDragging ? 2 : undefined,
    position: 'relative',
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ fontFamily: '"Poppins", sans-serif', display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            '&:active': { cursor: 'grabbing' },
            '&:hover': { color: '#00C896' },
            touchAction: 'none',
          }}
          title="Drag to reorder"
        >
          <DragIndicator fontSize="small" />
        </Box>
        <span>{category.name}</span>
      </TableCell>
      <TableCell sx={{ fontFamily: '"Poppins", sans-serif', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>{category.icon || '🍽️'}</TableCell>
      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
        {category.image_url ? (
          <Box
            component="img"
            src={category.image_url}
            alt={category.name}
            sx={{
              width: 40,
              height: 40,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid #3a3d52',
            }}
          />
        ) : (
          <Typography variant="caption" sx={{ color: '#6b7280' }}>{noImageLabel}</Typography>
        )}
      </TableCell>
      <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={() => onMoveUp(category.id)}
            disabled={isFirst}
            sx={{
              color: '#9ca3af',
              '&:hover': { color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.08)' },
              '&.Mui-disabled': { color: '#3a3d52' },
            }}
            title="Move up"
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onMoveDown(category.id)}
            disabled={isLast}
            sx={{
              color: '#9ca3af',
              '&:hover': { color: '#00C896', backgroundColor: 'rgba(0, 200, 150,0.08)' },
              '&.Mui-disabled': { color: '#3a3d52' },
            }}
            title="Move down"
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onEdit(category)}>
            <Edit />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(category)} sx={{ color: '#d32f2f' }}>
            <Delete />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};